// The HQ console's copy (spec/14) — the `hq` namespace. Loaded by the console's HQ build alone,
// through `@clavian/i18n/hq`: the venue and organisation console at `app.` carries none of it.
//
// The voice is BRAND.md §6's, and the words are the glossary's: HQ is the Operator's head office, an
// organisation runs venues, a station is never a terminal. The generic settings renderer's own words
// (Save, Reset to inherited, Changed elsewhere) are the console's (`console` namespace); this holds
// what only HQ says.
//
// A brand group's copy is filed under the group's id, and a property's under its name, as the
// console files a venue field's (`venueProfile.label`).
export const hqCopy = {
    signIn: {
        intro: 'Staff sign-in to the HQ console, the Operator’s head office.',
        footer: 'For Clavian platform operators only.',
    },
    shell: {
        area: 'HQ console',
        badge: 'HQ',
        mainNav: 'HQ',
    },
    // spec/14 §1, in its order. Only the sections that are built, and that the role holds a permission
    // of, are shown.
    nav: {
        live: 'Live',
        leads: 'Leads',
        organizations: 'Organisations',
        venues: 'Venues',
        subscriptions: 'Subscriptions',
        payments: 'Payments',
        catalogue: 'Catalogue',
        content: 'Content',
        loyalty: 'Loyalty',
        promotions: 'Promotions',
        players: 'Players',
        reports: 'Reports',
        broadcasts: 'Broadcasts',
        fleet: 'Fleet',
        support: 'Support',
        settings: 'Platform settings',
        team: 'HQ team',
        audit: 'Audit log',
    },
    home: {
        title: 'HQ console',
        nothingOpen: 'Nothing in the HQ console is open to your role yet. An HQ owner decides what each HQ role can do.',
    },
    // HQ Live (spec/14 §2, F-ANL-02): the Platform right now, aggregate only.
    live: {
        title: 'Live',
        intro: 'The Platform right now, across every organisation: rebuilt every minute, aggregate only.',
        failed: 'HQ Live could not be loaded.',
        rebuilding: 'The live figures are being rebuilt. They are back within a minute.',
        playersInSession: 'Players in session',
        stationsInUse: 'Stations in use',
        ofTotal: '/ {{total}}',
        inUse: '{{percent}}% of stations in use',
        venuesOnline: 'Venues online',
        organizationsOnline: '{{online}} of {{total}} organisations online',
        takingsToday: 'Takings today',
        takingsHint: 'Revenue so far in each venue’s own business day',
        newPlayersToday: 'New players today',
        offline: 'Venues offline',
        offlineHint: 'Every station offline for more than 10 minutes during the venue’s opening hours.',
        noneOffline: 'No venue is offline during its opening hours.',
        busiest: 'Busiest venues now',
        noneBusy: 'No sessions are running.',
        stationsOnline: '{{online}} of {{total}} online',
        computedAt: 'Figures as at {{at}}.',
        column: {
            venue: 'Venue',
            sessions: 'Sessions now',
            stations: 'Stations',
            offlineSince: 'Offline since',
        },
    },
    notForYou: {
        title: 'This console is for HQ staff',
        body: 'Your account has no HQ role, so there is nothing for it here. Venue and organisation staff use the venue console.',
        toConsole: 'Go to the venue console',
        signOut: 'Sign out',
    },
    notPermitted: {
        title: 'Not open to your role',
        body: 'Your HQ role cannot open this part of the HQ console.',
    },
    notFound: {
        home: 'Back to the HQ console',
    },
    // The sales pipeline: the cafés Clavian is trying to sign (Today, Pipeline, Funnel, one lead). A light pipeline, not a
    // CRM. Written for the person doing the selling: plain words, no internal names.
    leads: {
        title: 'Leads',
        intro: 'The cafés you are trying to sign, how far each has got, and what to do next.',
        sections: 'Leads',
        tab: { today: 'Today', pipeline: 'Pipeline', funnel: 'Funnel' },
        unavailable: 'The leads could not be loaded.',
        lead: {
            back: 'All leads',
            notFoundTitle: 'Lead not found',
            notFound: 'This lead does not exist any more. It may have been deleted.',
            unavailable: 'This lead could not be loaded.',
            loading: 'Loading lead',
            messageWhatsApp: 'Message on WhatsApp',
            messageInstagram: 'Message on Instagram',
            moreActions: 'More actions',
            whatsAppNoMobile: 'This lead has no mobile number, so WhatsApp is not available. Add one under Edit.',
            whatsAppDnc: 'This lead asked not to be contacted.',
            logActivity: 'Log activity',
            changeStage: 'Change stage',
            edit: 'Edit',
            convert: 'Convert to organisation',
            delete: 'Delete lead',
            markReplied: 'Mark as replied',
            markedReplied: '{{name}} is marked as replied.',
            stats: { rating: 'Google rating', reviews: 'Google reviews', stations: 'Stations', sinceContact: 'Days since last contact', noContact: 'Not yet', ofFive: 'of 5' },
            facts: {
                phone: 'Phone',
                email: 'Email',
                instagram: 'Instagram',
                address: 'Address',
                map: 'Map',
                platforms: 'Platforms',
                software: 'Software now',
                hook: 'Opening line',
                source: 'Source',
                created: 'Added',
                contact: 'Contact',
                openMap: 'Open the map',
                copy: 'Copy',
                copyPhone: 'Copy the phone number',
                copied: 'Copied',
                landline: 'Landline, so no WhatsApp',
                missing: 'Not recorded yet: {{names}}.',
                missingAction: 'Add details',
            },
            followUp: {
                title: 'Next follow-up',
                none: 'No follow-up set',
                label: 'Follow up on',
                tomorrow: 'Tomorrow',
                in3: 'In 3 days',
                nextWeek: 'Next week',
                clear: 'Clear',
                save: 'Save date',
                saved: 'Follow-up saved.',
                cleared: 'Follow-up cleared.',
                overdue: 'Overdue, was due {{when}}',
                failed: 'Could not save the follow-up.',
            },
            owner: { title: 'Owner', label: 'Owner', nobody: 'Nobody yet', saved: 'Owner saved.', failed: 'Could not change the owner.' },
            dnc: {
                title: 'Do not contact',
                description: 'Turn this on if the café asked not to be contacted. It will not be messaged or shown in Today.',
                on: 'Do not contact',
                since: 'Marked {{when}}',
                reasonTitle: 'Do not contact',
                reasonDescription: 'Say why, so nobody contacts them by mistake.',
                reason: 'Reason',
                reasonRequired: 'Say why.',
                confirm: 'Mark do not contact',
                clearTitle: 'Allow contact again?',
                clearBody: 'This lead will show up in Today and can be messaged again.',
                clearConfirm: 'Allow contact',
                cancel: 'Cancel',
                saved: 'Marked do not contact.',
                cleared: 'Contact allowed again.',
                failed: 'Could not change this.',
                badge: 'Do not contact',
            },
            converted: { title: 'This lead is now a customer', body: '{{name}} was set up as an organisation.', open: 'Open the organisation' },
            lost: 'Lost: {{reason}}',
            deleteDialog: {
                title: 'Delete this lead?',
                body: 'This erases {{name}} with its contact details and its whole timeline. It cannot be undone.',
                confirm: 'Delete lead',
                cancel: 'Keep it',
                deleted: '{{name}} was deleted.',
                failed: 'Could not delete the lead.',
            },
            timeline: {
                title: 'Timeline',
                empty: 'Nothing has happened with this lead yet.',
                noteLabel: 'Add a note',
                notePlaceholder: 'What happened, or what to remember',
                addNote: 'Add note',
                noteFailed: 'Could not add the note.',
                more: 'Show earlier',
                by: 'by {{who}}',
                via: 'via {{channel}}',
                template: 'Template: {{name}}',
                firstMessage: 'First message',
                stageChange: '{{from}} → {{to}}',
                unavailable: 'The timeline could not be loaded.',
                system: 'Clavian',
            },
        },
        message: {
            title: 'Send a message',
            channel: 'Channel',
            template: 'Template',
            noTemplate: 'Write my own',
            body: 'Message',
            to: 'To',
            missing: 'Not filled in: {{names}}. Edit the message before sending.',
            unknown: 'Not recognised: {{names}}.',
            noTemplates: 'No templates for this channel yet. You can write your own message.',
            firstLeft: 'First messages left today: {{left}} of {{cap}}',
            openWhatsApp: 'Open WhatsApp',
            openInstagram: 'Copy and open Instagram',
            openEmail: 'Record the email',
            cancel: 'Cancel',
            copy: 'Copy message',
            copiedPlain: 'Message copied.',
            copyFailed: 'Could not copy the message.',
            recordOnly: 'I’ve sent it, just record it',
            noPhone: 'This lead has no mobile number.',
            noHandle: 'This lead has no Instagram handle.',
            noEmail: 'This lead has no email address.',
            recorded: 'Recorded.',
            recordedFollowUp: 'Recorded. Follow up on {{date}}.',
            copied: 'Message copied. Paste it into the Instagram chat.',
            failed: 'Could not record the message.',
            popupBlocked: 'The chat could not open by itself. Allow pop-ups for this site and try again.',
            empty: 'Write the message first.',
        },
        activity: {
            title: 'Log activity',
            description: 'Keep a note of what happened with {{name}}.',
            kind: 'What happened',
            body: 'Details',
            bodyRequired: 'Write a note.',
            when: 'When',
            future: 'That time is in the future.',
            confirm: 'Log it',
            cancel: 'Cancel',
            saved: 'Added to the timeline.',
            failed: 'Could not log this.',
            kinds: { note: 'Note', call: 'Call', visit: 'Visit', reply: 'Reply received' },
        },
        stageDialog: {
            title: 'Change stage',
            description: '{{name}} is {{stage}} now.',
            stage: 'Move to',
            note: 'Note',
            noteHint: 'Optional. It goes on the timeline.',
            reason: 'Why was it lost?',
            confirm: 'Move',
            cancel: 'Cancel',
            failed: 'Could not change the stage.',
            reasonRequired: 'Pick a reason.',
        },
        convert: {
            title: 'Convert to organisation',
            description: 'This creates the café’s organisation and sends the owner an invitation to finish setting up.',
            name: 'Business name',
            owner: 'Invite the owner by',
            byPhone: 'Phone',
            byEmail: 'Email',
            phone: 'Owner’s phone number',
            email: 'Owner’s email address',
            kind: 'Type',
            confirm: 'Create organisation',
            cancel: 'Cancel',
            nameRequired: 'Enter the business name.',
            contactRequired: 'Enter the owner’s phone number or email address.',
            slugTaken: 'An organisation with a similar name already exists. Change the business name.',
            converted: 'This lead already became an organisation.',
            failed: 'Could not create the organisation.',
            done: '{{name}} was created and its owner invited.',
        },
        today: {
            title: 'Today',
            stats: { dueToday: 'Due today', overdue: 'Overdue', firstLeft: 'First messages left today', of: 'of {{cap}}' },
            due: { title: 'Follow-ups due', hint: 'Overdue first.', empty: 'Nothing is due. Nice work.' },
            fresh: { title: 'New leads to contact', hint: 'Best prospects first.', empty: 'No new leads waiting.' },
            emptyAll: 'Nothing to do today',
            emptyAllHint: 'Add leads or import your spreadsheet, then come back here for the day’s list.',
            goPipeline: 'Open the pipeline',
            message: 'Message',
            snooze: 'Snooze',
            snoozeTomorrow: 'Tomorrow',
            snooze3: 'In 3 days',
            snoozed: 'Follow-up moved to {{date}}.',
            snoozeFailed: 'Could not move the follow-up.',
            open: 'Open',
            rowActions: 'Actions for {{name}}',
            lastContact: 'Last contact {{when}}',
            neverContacted: 'Not contacted yet',
            dueWhen: 'Due {{when}}',
            overdueWhen: 'Overdue, due {{when}}',
            unavailable: 'Today could not be loaded.',
            listLabelDue: 'Follow-ups due',
            listLabelFresh: 'New leads to contact',
        },
        funnel: {
            title: 'Funnel',
            unavailable: 'The funnel could not be loaded.',
            empty: 'No leads yet',
            emptyHint: 'The funnel fills in as you add leads and contact them.',
            totals: { leads: 'Leads', contacted: 'Contacted', replied: 'Replied', won: 'Won' },
            stagesTitle: 'Leads by stage',
            stagesLabel: 'Leads by stage',
            outOfPlay: 'Out of play',
            outOfPlayLabel: 'Leads out of play',
            citiesTitle: 'By city',
            citiesCaption: 'Leads by city',
            city: 'City',
            total: 'Leads',
            open: 'Open',
            won: 'Won',
            conversion: 'Conversion',
            templatesTitle: 'Messages and replies',
            templatesCaption: 'Replies by template',
            template: 'Template',
            channel: 'Channel',
            sent: 'Sent',
            replied: 'Replied',
            replyRate: 'Reply rate',
            noTemplate: 'Written by hand',
            noCities: 'No city recorded yet.',
            noTemplates: 'No messages sent yet.',
            noRate: '—',
        },
        pipeline: {
            title: 'Pipeline',
            caption: 'Leads',
            newLead: 'New lead',
            importLeads: 'Import',
            templates: 'Templates',
            view: { label: 'View', board: 'Board', table: 'Table' },
            search: 'Search by name, area, Instagram or phone',
            filters: { label: 'Filters', stage: 'Stage', city: 'City', priority: 'Priority', owner: 'Owner', sort: 'Sort by' },
            stageOpen: 'Open',
            moreCities: 'More cities',
            anyCity: 'More cities…',
            anyOwner: 'Anyone',
            unassigned: 'Nobody yet',
            doNotContact: 'Do not contact',
            clearFilters: 'Clear filters',
            sort: { followUp: 'Follow-up due', reviews: 'Most reviews', updated: 'Recently updated' },
            priorityChip: 'Priority {{priority}}',
            noMatchTitle: 'No lead matches',
            noMatch: 'Nothing matches these filters. Clear a filter or change the search.',
            emptyTitle: 'No leads yet',
            empty: 'Add the cafés you want to sign. Import your spreadsheet in one go, or add them one at a time.',
            more: 'Show more',
            column: { count_one: '{{count}} lead', count_other: '{{count}} leads', empty: 'Nothing here', more: 'Show more' },
            parkedStrip: 'Not in play',
            board: 'Pipeline board',
            cardMenu: 'Actions for {{name}}',
            moveTo: 'Move to',
            moveToStage: 'Move to {{stage}}',
            moved: '{{name}} is now {{stage}}.',
            moveFailed: 'Could not move the lead.',
            whatsAppOk: 'Can message on WhatsApp',
            landline: 'Landline, no WhatsApp',
            dnc: 'Do not contact',
            followUp: 'Follow up {{when}}',
            followUpOverdue: 'Follow-up was due {{when}}',
            noFollowUp: 'No follow-up set',
            reviews_one: '{{count}} review',
            reviews_other: '{{count}} reviews',
            noRating: 'No rating',
            columns: {
                business: 'Business',
                city: 'City',
                area: 'Area',
                stage: 'Stage',
                priority: 'Priority',
                rating: 'Rating',
                reviews: 'Reviews',
                nextFollowUp: 'Next follow-up',
                lastContacted: 'Last contacted',
                owner: 'Owner',
                phone: 'Phone',
            },
            never: 'Never',
            none: '—',
        },
        lost: {
            title: 'Why was it lost?',
            description: 'A short reason helps you see later what is not working.',
            reason: 'Reason',
            confirm: 'Mark as lost',
            cancel: 'Cancel',
            failed: 'Could not mark the lead as lost.',
        },
        stage: {
            open: 'Open',
            new: 'New',
            contacted: 'Contacted',
            replied: 'Replied',
            demo: 'Demo',
            proposal: 'Proposal',
            won: 'Won',
            lost: 'Lost',
            parked: 'Parked',
        },
        priority: { a: 'A', b: 'B', c: 'C', label_a: 'Priority A, the best prospects', label_b: 'Priority B', label_c: 'Priority C' },
        channel: { whatsapp: 'WhatsApp', instagram: 'Instagram', email: 'Email', call: 'Call', visit: 'Visit' },
        kind: {
            message: 'Message sent',
            call: 'Call',
            visit: 'Visit',
            reply: 'Reply',
            note: 'Note',
            stage_change: 'Stage changed',
            import: 'Imported',
            follow_up: 'Follow-up',
            do_not_contact: 'Do not contact',
            conversion: 'Became a customer',
        },
        lostReason: {
            price: 'Too expensive',
            'happy-with-current-software': 'Happy with what they use',
            'closed-down': 'Closed down',
            'no-reply': 'Never replied',
            'not-a-fit': 'Not a fit',
            other: 'Something else',
        },
        source: { manual: 'Added by hand', import: 'Imported', website: 'From the website' },
        form: {
            createTitle: 'New lead',
            editTitle: 'Edit lead',
            create: 'Add lead',
            save: 'Save changes',
            cancel: 'Cancel',
            businessName: 'Business name',
            phone: 'Phone',
            phoneHint: 'As you have it: 98765 43210 or +91 98765 43210.',
            phoneNormalised: 'Saved as {{phone}}',
            phoneLandline: 'This looks like a landline, so WhatsApp will not work. Add a mobile number if you can.',
            phoneInvalid: 'That does not look like an Indian phone number.',
            city: 'City',
            area: 'Area',
            priority: 'Priority',
            priorityHint: 'A is the best prospect. C is the default.',
            contactName: 'Contact name',
            owner: 'Owner',
            noOwner: 'Nobody yet',
            more: 'More options',
            less: 'Fewer options',
            email: 'Email',
            instagram: 'Instagram',
            instagramHint: 'A handle or a link.',
            addressHint: 'Address or landmark',
            stationCount: 'Number of stations',
            platforms: 'Platforms',
            platformsHint: 'Separate with commas: PC, PS5, VR.',
            currentSoftware: 'Software they use now',
            googleRating: 'Google rating',
            googleRatingHint: 'One decimal, like 4.6.',
            googleReviews: 'Google reviews',
            mapUrl: 'Map link',
            hook: 'Opening line',
            hookHint: 'Something specific to say to them. It is added to the message where the hook placeholder is.',
            businessRequired: 'Enter the business name.',
            ratingInvalid: 'A rating from 0 to 5, with one decimal.',
            numberInvalid: 'Enter a whole number.',
            phoneTaken: 'Another lead already has this phone number.',
            saved: '{{name}} was saved.',
            created: '{{name}} was added.',
            failed: 'Could not save the lead.',
        },
        import: {
            title: 'Import leads',
            description: 'Add many cafés at once from a spreadsheet. Nothing is added until you confirm.',
            drop: 'Drop a CSV or Excel file',
            dropHint: 'CSV or XLSX, up to 5 MB.',
            fileLabel: 'Spreadsheet file',
            columnsHelp: 'Which columns can I use?',
            columnsBody: 'Put headings in the first row. We recognise business name, phone, city, area, contact, email, Instagram, address, stations, platforms, current software, Google rating, Google reviews, map link and hook. Headings can be in any case. Other columns are ignored.',
            reading: 'Reading {{file}}…',
            total: 'Rows in the file',
            willAdd: 'Will be added',
            duplicatesCount: 'Already in your leads',
            refusedCount: 'Cannot be read',
            duplicates: 'Skipped as duplicates',
            refused: 'Rows we could not use',
            row: 'Row',
            name: 'Business',
            reason: 'Reason',
            matched: 'Columns we used',
            ignored: 'Columns we ignored',
            none: 'None',
            confirm_one: 'Import {{count}} lead',
            confirm_other: 'Import {{count}} leads',
            nothingToAdd: 'There is nothing new to add from this file.',
            chooseAnother: 'Choose another file',
            cancel: 'Cancel',
            close: 'Close',
            done_one: '{{count}} lead was added.',
            done_other: '{{count}} leads were added.',
            tooBig: 'That file is over 5 MB. Split it and import the parts one by one.',
            unreadable: 'That file could not be read. Save it as CSV or XLSX and try again.',
            tooManyRows: 'That file has too many rows. Import it in smaller parts.',
            wrongType: 'Choose a CSV or XLSX file.',
            failed: 'Could not import the file.',
        },
        templates: {
            title: 'Message templates',
            description: 'Ready-made first messages. Placeholders are filled in for each lead.',
            newTemplate: 'New template',
            showArchived: 'Show archived',
            archivedBadge: 'Archived',
            listLabel: 'Templates',
            empty: 'No templates yet.',
            emptyHint: 'Start with a short, polite first message and edit it to sound like you.',
            starter: 'Start with our first-message template',
            starterName: 'First message',
            // Placeholders are written [[like this]] here so the copy is not read as a variable; the form turns them into the real ones.
            starterBody: 'Hi [[contact]], this is [[sender]] from Clavian. We make software that helps gaming cafés like [[business]] run session timing, billing, stations and the kiosk without the manual work. [[hook]] Would you be open to a quick look at how it works?',
            name: 'Template name',
            channel: 'Channel',
            body: 'Message',
            bodyHint: 'Click a placeholder to add it where your cursor is.',
            placeholders: 'Placeholders',
            preview: 'Preview',
            previewHint: 'Shown with a sample café.',
            sample: { business: 'Level Up Lounge', contact: 'Anika', city: 'Pune', area: 'Koregaon Park', hook: 'I saw your 4.6 rating on Google.', sender: 'Asha' },
            edit: 'Edit',
            archive: 'Archive',
            save: 'Save template',
            create: 'Create template',
            back: 'Back to templates',
            saved: 'Template saved.',
            archived: 'Template archived.',
            archiveFailed: 'Could not archive the template.',
            failed: 'Could not save the template.',
            close: 'Close',
            nameRequired: 'Give the template a name.',
            bodyRequired: 'Write the message.',
            unknownPlaceholders: 'These placeholders are not recognised: {{names}}.',
        },
    },
    // Organisations (spec/14 §3.1, F-HQ-02, ADR-0046): the list with each owner's status, inviting a café with
    // its owner's invitation, and one organisation's page with its owner. The record is read only here.
    // Subscriptions → Add-ons: the catalogue of add-ons and the price of each (F-BOOT-17).
    subscriptions: {
        title: 'Subscriptions',
        intro: 'The add-ons Clavian offers, and the price of each per PC a month. A new price applies to add-ons attached after it; one already attached keeps the price it was attached at.',
        loading: 'Loading the add-ons',
        unavailable: 'The add-ons could not be loaded.',
        empty: {
            title: 'No add-on has a price yet',
            body: '{{name}} cannot be attached to an organisation until you set its price per PC a month.',
            action: 'Set {{name}}’s price',
        },
        addons: {
            title: 'Add-ons',
            intro: 'Each add-on is priced per licensed PC a month. Offering it makes it available to attach from an organisation’s page.',
        },
        columns: { name: 'Add-on', pricing: 'Priced', price: 'Price', offered: 'Offered', updated: 'Last changed', actions: 'Actions' },
        pricing: { per_pc: 'Per PC, each month' },
        perPcMonth: '{{price}} per PC a month',
        offered: 'Offered',
        notOffered: 'Not offered',
        edit: 'Change price',
        dialog: {
            offerTitle: 'Set {{name}}’s price',
            editTitle: 'Change {{name}}’s price',
            intro: 'The price is for each PC, each month.',
            formLabel: '{{name}} price',
            price: 'Price per PC a month ({{currency}})',
            priceHint: 'Type it as you would write it, for example 150 or 149.99.',
            currency: 'Currency',
            currencyHint: 'Three letters, for example INR.',
            currencyFixed: 'Priced in {{currency}}. Organisations in another currency cannot have it attached.',
            example: '{{pcs}} PCs at {{price}} come to {{total}} a month.',
            offered: 'Offer it to organisations',
            snapshotNote: 'Organisations that already have it keep the price they were attached at.',
            cancel: 'Cancel',
            offer: 'Set the price',
            save: 'Save',
            saved: '{{name}} saved',
            failed: 'The price could not be saved. Try again.',
            errors: {
                currency: 'Write the currency as three letters, for example INR.',
                empty: 'Write a price. Write 0 if it is free.',
                invalid: 'Write the price as a number, for example 150 or 149.99.',
                fraction: 'That has more decimal places than the currency has.',
                whole: 'This currency has no decimals. Write a whole number.',
                tooLarge: 'That price is higher than the system accepts.',
            },
        },
    },
    organizations: {
        title: 'Organisations',
        intro: 'Every organisation on the platform, newest first. Head office invites each café and its owner; it does not join its staff.',
        create: 'Invite a café',
        caption: 'Organisations',
        emptyTitle: 'No organisations yet',
        empty: 'No organisation yet. Invite the first café.',
        notJoinedEmptyTitle: 'Every owner has joined',
        notJoinedEmpty: 'No café is waiting on its owner.',
        filter: {
            label: 'Show',
            all: 'All',
            notJoined: 'Owner not joined yet',
        },
        noMatch: 'No organisation matches.',
        search: 'Search by name, slug or legal name',
        figures: {
            label: 'The platform at a glance',
            total: 'Organisations',
            loaded: 'Of those loaded so far',
            brands: 'Franchise brands',
            franchisees_one: '{{count}} franchisee',
            franchisees_other: '{{count}} franchisees',
            independents: 'Independents',
            attention: 'Not active',
            attentionHint: 'Pending or suspended',
            notJoined: 'Owner not joined',
            notJoinedHint: 'Invited, expired, withdrawn or not invited',
        },
        view: {
            label: 'View',
            list: 'List',
            tree: 'Franchise tree',
        },
        tree: {
            noFranchisees: 'No franchisees yet.',
            independents: 'Independent and unlinked',
        },
        more: 'Show more',
        unavailable: 'The organisations could not be loaded.',
        column: {
            name: 'Organisation',
            legalName: 'Legal name',
            kind: 'Kind',
            status: 'Status',
            owner: 'Owner',
            hqAccess: 'HQ access',
            created: 'Created',
            setup: 'Setup',
        },
        // Where each organisation stands in its setup (spec/14 §3.2, F-HQ-03): reading it is recorded in each organisation.
        setup: {
            live: 'Live',
            noVenue: 'No venue yet',
            progress: '{{done}} of {{total}}',
            stuck: '{{done}} of {{total}} · stuck at {{step}}',
            loading: 'Reading setup…',
            unavailable: 'Unknown',
            title: 'Getting ready to open',
            trackerStep: {
                firstVenue: 'Venue',
                openingHours: 'Hours',
                price: 'Price',
                stationPaired: 'PC',
            },
            next: {
                firstVenue: 'Next: the first venue. The owner adds it in setup.',
                openingHours: 'Next: opening hours for {{venue}}.',
                price: 'Next: a price for {{venue}}.',
                stationPaired: 'Next: pair the first PC at {{venue}}.',
            },
            allDone: 'Live. Every essential is done.',
            venuesLabel: 'Venue essentials',
            intro: 'Where this organisation stands before it opens: a first venue, then each venue’s essentials. Reading it is recorded in the organisation’s audit log.',
            venueEssentials: '{{done}} of {{total}} essentials',
            venueMissing: 'Missing: {{steps}}',
            venueReady: 'Ready to go live',
            venueOpen: 'Open for business',
            noVenues: 'No venue yet.',
            step: {
                firstVenue: 'a first venue',
                openingHours: 'opening hours',
                price: 'a first pass',
                stationPaired: 'pairing a PC',
            },
        },
        // Where an organisation's owner is (ADR-0046): the list's column and the page's badge.
        ownerStatus: {
            joined: 'Joined',
            invited: 'Invited',
            expired: 'Expired',
            withdrawn: 'Withdrawn',
            none: 'Not invited',
        },
        kind: {
            franchisor: 'Franchisor',
            franchisee: 'Franchisee',
            independent: 'Independent',
        },
        kindHint: {
            franchisor: 'A brand that owns venues and has franchisees.',
            franchisee: 'A franchisor’s own-entity outlet, linked to its brand.',
            independent: 'A standalone owner, no franchisor and no franchisees.',
        },
        status: {
            pending: 'Pending',
            active: 'Active',
            suspended: 'Suspended',
        },
        hqAccess: {
            full: 'Full',
            aggregate_only: 'Aggregates only',
        },
        hqAccessHint: {
            full: 'Its rows as well as its totals, each read of its customer data recorded with a reason.',
            aggregate_only: 'Its totals and no row-level customer data, where its agreement limits head office.',
        },
        new: {
            title: 'Invite a café',
            intro: 'Give the café’s business name and its owner’s email or phone. The owner gets a welcome invitation, creates their account and completes the rest — the legal name, PAN, registered address and their first venue — in a short guided setup.',
            back: 'All organisations',
            section: {
                cafe: 'The café',
                owner: 'Owner',
                more: 'More details (optional)',
                address: 'Registered address',
            },
            field: {
                name: 'Business name',
                slug: 'Slug',
                legalName: 'Legal name',
                kind: 'Kind',
                pan: 'PAN',
                hqAccess: 'HQ access level',
                ownerBy: 'Invite the owner by',
                email: 'Owner’s email address',
                phoneNumber: 'Owner’s phone number',
            },
            hint: {
                name: 'What the café is called day to day — on its console, its venues and its receipts.',
                owner: 'They get a welcome invitation with a link that works for seven days, and create their account with this address or number.',
                // While phone sign-in is turned off: by email only.
                ownerEmail: 'They get a welcome invitation with a link that works for seven days, and create their account with this address.',
                more: 'Fill in anything you already know. The owner can complete or correct the legal name, PAN and registered address during setup.',
                slug: 'Lower-case letters, digits and single hyphens, unique on the platform, for example playground-gaming. Written from the business name unless you change it.',
                legalName: 'The registered legal entity, as on its documents. Leave it for the owner if you do not have it.',
                pan: 'Five capital letters, four digits and a capital letter, for example AAACP1234Q. Leave it empty if you do not have it to hand.',
                address: 'Optional. Once you start it, give the first line, the city, the state and the PIN code.',
                hqAccess: 'Full unless the organisation’s agreement limits what head office sees.',
                phoneNumber: 'With the country code, for example +91 98765 43210.',
            },
            channel: {
                email: 'Email',
                phone: 'Phone',
            },
            submit: 'Send the invitation',
            cancel: 'Cancel',
            required: 'Fill this in.',
            choose: 'Choose one.',
            invalidEmail: 'Write an email address, for example owner@example.com.',
            invalidPhone: 'Write the number with its country code, for example +91 98765 43210.',
            refused: 'Some values were refused. They are marked below.',
            slugTaken: 'Another organisation already has this slug. Choose another.',
            forbidden: 'Your HQ role cannot invite a café.',
        },
        page: {
            back: 'All organisations',
            unavailable: 'This organisation could not be loaded.',
            noSuch: 'No organisation has this id.',
            record: 'Record',
            readOnly: 'Read only here. The owner completes or corrects the legal name, PAN and registered address in their settings; changing the kind or the HQ access level is still to come.',
            notRecorded: 'Not recorded',
            field: {
                name: 'Trade name',
                slug: 'Slug',
                legalName: 'Legal name',
                kind: 'Kind',
                pan: 'PAN',
                registeredAddress: 'Registered address',
                hqAccess: 'HQ access',
                status: 'Status',
                currency: 'Currency',
                createdAt: 'Created',
                id: 'Organisation id',
            },
            elsewhere: 'Elsewhere in the HQ console',
            elsewhereTip: 'Opening its venues or its settings is recorded in its audit log.',
            venues: 'Its venues',
            venuesHint: 'Venues and listing flags',
            settings: 'Its settings and locks',
            settingsHint: 'Set or lock a setting',
            details: 'Details',
            meta: '{{kind}} · {{currency}} · since {{date}}',
            missing: 'Missing {{what}} · the owner adds them in settings',
            and: 'and',
            missingWhat: {
                legalName: 'legal name',
                pan: 'PAN',
                registeredAddress: 'address',
            },
            stat: {
                venues: 'Venues',
                open: 'Open now',
                steps: 'Setup steps',
                owner: 'Owner',
                of: 'of {{total}}',
            },
            toOwner: 'Go to the owner',
            toVenues: 'See its venues',
        },
        // Online payments on the organisation's page: the commission and the café's payout details.
        payments: {
            title: 'Online payments',
            unavailable: 'This café’s online payments setup could not be loaded.',
            notSetUp: 'Online payments are not set up on this platform yet.',
            cancel: 'Cancel',
            status: {
                pending: 'Being verified',
                action_required: 'Needs attention',
                active: 'Active',
                rejected: 'Rejected',
                suspended: 'Suspended',
            },
            businessType: {
                individual: 'Individual',
                proprietorship: 'Proprietorship',
                partnership: 'Partnership',
                private_limited: 'Private limited',
                llp: 'LLP',
            },
            commission: {
                title: 'Commission',
                value: '{{percent}}% of each online payment',
                explain: 'Taken from each online payment, together with the payment provider’s fee. The café sees this figure but cannot change it.',
                edit: 'Edit commission',
                label: 'Commission (%)',
                hint: 'From 0 to 50, with up to two decimals.',
                invalid: 'Write a percentage from 0 to 50, with up to two decimals.',
                save: 'Save commission',
                saved: 'Commission saved',
                failed: 'The commission could not be saved. Try again.',
            },
            payouts: {
                title: 'Payouts',
                none: 'This café has to be set up to receive its share of online payments. Until then, online payments stay closed for its venues.',
                setUp: 'Set up payouts',
                update: 'Update payout details',
                check: 'Check status',
                checked: 'Status checked',
                checkFailed: 'The status could not be checked. Try again.',
                providerDown: 'The payment provider could not be reached. Try again in a moment.',
                business: 'Business',
                bank: 'Bank account',
                bankValue: 'Account ending {{last4}} · {{ifsc}}',
                pan: 'PAN',
                panValue: 'PAN ending {{last4}}',
                gstin: 'GSTIN',
                contact: 'Contact',
                contactValue: '{{name}} · {{email}} · {{phone}}',
                checkedAt: 'Last checked {{when}}',
                neverChecked: 'Not checked yet',
            },
            dialog: {
                setUpTitle: 'Set up payouts',
                updateTitle: 'Update payout details',
                intro: 'Where the payment provider pays this café’s share of online payments.',
                formLabel: 'Payout details',
                businessName: 'Business name',
                businessType: 'Business type',
                pan: 'PAN',
                gstin: 'GSTIN',
                contactName: 'Contact name',
                contactEmail: 'Contact email',
                contactPhone: 'Contact mobile',
                holder: 'Account holder name',
                account: 'Bank account number',
                accountAgain: 'Confirm bank account number',
                ifsc: 'IFSC',
                retype: 'Type it again. Only the last four digits are kept, so the full number cannot be shown here.',
                privacy: 'The account number and PAN go to the payment provider and are not stored by us. Only the last four digits are kept.',
                save: 'Save payout details',
                saved: 'Payout details saved',
                providerDown: 'The payment provider could not be reached. Try again in a moment.',
                unavailable: 'Online payments are not available on this platform, or the payment provider does not pay through payees.',
                refused: 'The payment provider refused these details.',
                failed: 'The payout details could not be saved. Try again.',
                error: {
                    required: 'This is required.',
                    businessName: 'Write at least three characters.',
                    pan: 'A PAN is ten characters: five letters, four digits, a letter.',
                    gstin: 'A GSTIN is fifteen characters. Check it, or leave it empty.',
                    email: 'Write an email address, for example name@example.com.',
                    phone: 'Write a ten-digit mobile number.',
                    account: 'A bank account number is 9 to 18 digits.',
                    accountMatch: 'The two account numbers do not match.',
                    ifsc: 'An IFSC is eleven characters: four letters, a zero, then six letters or digits.',
                },
            },
        },
        // Add-ons on the organisation's page (F-BOOT-17): Clavian Boot, priced per PC a month, attached for a number of PCs.
        addons: {
            title: 'Add-ons',
            bootName: 'Clavian Boot',
            intro: 'Extras the organisation pays for each month on top of its plan. The price per PC is fixed when an add-on is attached; a later price change applies to add-ons attached after it.',
            unavailable: 'This organisation’s add-ons could not be loaded.',
            status: {
                active: 'Active',
                cancelled: 'Cancelled',
            },
            notAttached: '{{name}} is not attached to this organisation.',
            attach: 'Attach {{name}}',
            pcs_one: '{{count}} PC',
            pcs_other: '{{count}} PCs',
            licensed: 'PCs licensed',
            unitPrice: 'Price per PC',
            unitPriceValue: '{{price}} a month',
            monthlyCharge: 'Monthly charge',
            since: 'Since',
            total: 'Monthly total',
            ended: 'Ended',
            endedRow: '{{name}} · {{pcs}} · {{from}} to {{to}}',
            changePcs: 'Change PCs',
            cancelAddon: 'Cancel add-on',
            noPrice: '{{name}} has no price yet, so it cannot be attached.',
            notOffered: '{{name}} is not offered at the moment, so it cannot be attached.',
            currencyDiffers: '{{name}} is priced in {{priced}}, and this organisation trades in {{currency}}, so it cannot be attached.',
            dialog: {
                attachTitle: 'Attach {{name}}',
                attachIntro: 'The organisation can use it on this many PCs from now on, and is charged for each of them every month.',
                changeTitle: 'Change PCs',
                changeIntro: 'The price per PC stays the one it was attached at.',
                quantity: 'PCs',
                quantityHint: 'How many PCs it may use, from 1 to 10,000.',
                quantityInvalid: 'Write a whole number of PCs from 1 to 10,000.',
                preview: '{{price}} × {{pcs}} = {{total}} a month',
                cancel: 'Cancel',
                attach: 'Attach',
                save: 'Save',
                attached: '{{name}} attached',
                saved: 'PCs changed',
                failed: 'The add-on could not be saved. Try again.',
            },
            cancelDialog: {
                title: 'Cancel {{name}}?',
                body: 'It stops now: the organisation can no longer use it and is no longer charged for it. To use it again, attach it again at the price of the day.',
                keep: 'Keep it',
                confirm: 'Cancel add-on',
                cancelled: '{{name}} cancelled',
                failed: 'The add-on could not be cancelled. Try again.',
            },
        },
        // Approving or declining a pending organisation (ADR-0019 §6, F-HQ-02).
        approval: {
            title: 'Awaiting approval',
            intro: 'This organisation has applied and is not yet active. Approve it to make it active, or decline it with a reason.',
            approve: 'Approve',
            decline: 'Decline',
            cancel: 'Cancel',
            reasonLabel: 'Reason for declining',
            reasonHint: 'Recorded in the organisation’s audit log.',
            reasonRequired: 'Give a reason for declining.',
            notPending: 'This organisation is no longer awaiting approval.',
            failed: 'The decision could not be recorded. Try again.',
        },
        // The owner (ADR-0046): whether they joined, what became of the invitation, and head office's actions on it.
        owner: {
            title: 'Owner',
            unavailable: 'Whether the owner has joined could not be read.',
            joined: '{{name}} joined on {{date}}.',
            joinedUnnamed: 'The owner joined on {{date}}.',
            joinedOn: 'Joined {{date}}',
            hasOwner: 'The organisation has an owner, so head office cannot invite one. Its owners invite owners.',
            none: 'No owner has been invited yet.',
            invitedOn: 'Invited on {{date}}, by {{by}} to {{destination}}.',
            invitedOnBy: 'Invited on {{date}} by {{name}}, by {{by}} to {{destination}}.',
            by: {
                email: 'email',
                phone: 'phone',
            },
            delivery: {
                sent: 'The message went by {{channel}}.',
                not_sent: 'The message could not be sent: copy the link and pass it on.',
                unknown: 'Whether the message went was not recorded.',
            },
            validUntil: 'The link works until {{date}}.',
            expired: 'The invitation to {{destination}} expired on {{date}} without being accepted.',
            withdrawn: 'The invitation to {{destination}} was withdrawn on {{date}}.',
            acceptedNoOwner: 'The invitation to {{destination}} was accepted, and nobody holds the owner role now.',
            invited: 'Invited {{destination}}, valid until {{expires}}.',
            sent: 'The invitation went by {{channel}}.',
            notSent: 'The invitation could not be sent. Pass this link on to the owner: it works once, for them alone, signed in with the address or number invited.',
            link: 'Invitation link',
            linkHint: 'It works once, for the person invited alone, signed in with the address or number invited.',
            linkCopied: 'Link copied.',
            channel: {
                email: 'email',
                sms: 'SMS',
                whatsapp: 'WhatsApp',
            },
            resend: 'Send a fresh invitation',
            resent: 'A fresh invitation went to {{destination}}.',
            resentNotSent: 'A fresh invitation was made. Its message could not be sent: copy the link and pass it on.',
            copyLink: 'Copy the link',
            withdraw: 'Withdraw',
            withdrawTitle: 'Withdraw the invitation?',
            withdrawBody: 'The link sent to {{destination}} stops working at once. You can send a fresh one afterwards.',
            withdrawConfirm: 'Withdraw it',
            keep: 'Keep it',
            withdrawnToast: 'Invitation withdrawn.',
            inviteOwner: 'Invite an owner',
            inviteSomeoneElse: 'Invite someone else',
            inviteTitle: 'Invite an owner',
            inviteIntro: 'They get a welcome invitation. It replaces any still waiting, so only the newest link works.',
            invite: 'Send the invitation',
            notPending: 'That invitation was already used, withdrawn or has expired.',
            failed: 'That did not work. Try again.',
            alreadyMember: 'That person is already on the organisation’s staff.',
            forbidden: 'Your HQ role cannot invite an owner.',
            // Phone sign-in turned off for now: invitations go by email.
            phoneOff: 'Invitations by phone aren’t available right now. Invite the owner by email instead.',
            phoneOffResend: 'This invitation went to a phone number, and invitations by phone aren’t available right now. Invite them again by email.',
        },
    },
    // Venues across organisations (spec/14 §3.3, F-HQ-04): the list, its filters, and the listing flags.
    venues: {
        title: 'Venues',
        intro: 'Every organisation’s venues, closed ones included, oldest first. Featured, Partner and Sponsored order the player app’s Venues list; a venue that is not publicly listed stays out of it whatever its flags say.',
        audited: 'Head office opening this list is recorded in the audit log of every organisation whose venues it shows.',
        filters: 'Filter the venues',
        field: {
            q: 'Name or slug',
            city: 'City or town',
            organization: 'Organisation',
            status: 'Status',
            publicListing: 'Public listing',
            featured: 'Featured',
            partner: 'Partner',
            sponsored: 'Sponsored',
        },
        everyOrganization: 'Every organisation',
        any: 'Any',
        yes: 'Yes',
        no: 'No',
        apply: 'Show venues',
        clear: 'Clear the filters',
        qTooShort: 'Write at least two letters.',
        caption: 'Venues',
        empty: 'No venue matches.',
        more: 'Show more',
        unavailable: 'The venues could not be loaded.',
        column: {
            venue: 'Venue',
            organization: 'Organisation',
            location: 'Location',
            hours: 'Hours',
            status: 'Status',
            publicListing: 'Public listing',
            flags: 'Listing flags',
        },
        listed: 'Listed',
        notListed: 'Not listed',
        noAddress: 'No address',
        hours: 'Opening hours',
        // What a screen reader hears for a row's control: its words, and which venue's row it is on.
        hoursFor: 'Opening hours, {{venue}}',
        flagFor: '{{flag}}, {{venue}}',
        flagOn: '{{flag}} is on for {{venue}}.',
        flagOff: '{{flag}} is off for {{venue}}.',
        flagFailed: '{{flag}} could not be changed for {{venue}}.',
        flagForbidden: 'Your HQ role cannot set listing flags.',
        flagGone: 'That venue is no longer there.',
    },
    // The game catalogue (spec/14 §6, F-CAT-01, F-CAT-07): HQ searches the metadata provider (IGDB) for
    // a base game and mirrors it into the platform catalogue, once, for every venue. A game is never a
    // "title" here — `title` is a different, deferred entity (the glossary).
    catalogue: {
        title: 'Catalogue',
        intro: 'The games and apps every venue can offer. Add a title once and it is ready for every venue.',
        // The Games | Apps switch under the page title (spec/14 §6).
        kindSwitch: 'Show',
        // The Games view's header: what a game in the catalogue is.
        games: {
            title: 'Games',
            intro: 'Base games mirrored from IGDB. An edition, a DLC or a bundle resolves to the base game it belongs to.',
        },
        kind: {
            game: 'Games',
            app: 'Apps',
            requests: 'Requests',
        },
        // A game's metadata by hand (F-CAT-01, F-CAT-07): edit a mirrored game, or add one IGDB does not know. Every
        // field changed here is kept through refreshes; archive hides it from venues adding titles.
        game: {
            editAction: 'Edit',
            editLabel: 'Edit {{game}}',
            createAction: 'Add by hand',
            createTitle: 'Add a game by hand',
            createIntro: 'For a game the metadata provider does not know, or while it is unavailable.',
            createHint: 'Pick its cover, hero and logo under Media once it is added. No refresh from IGDB ever changes it.',
            editTitle: 'Edit game',
            intro: 'Every field you change here is kept: the weekly refresh from IGDB leaves it exactly as you wrote it.',
            name: 'Name',
            nameRequired: 'Write the game’s name.',
            summary: 'Description',
            genres: 'Genres',
            labelsHint: 'Separate several with commas.',
            publisher: 'Publisher',
            ageRating: 'Age rating',
            ageRatingHint: 'For example PEGI 16 or ESRB M.',
            franchise: 'Series',
            releaseYear: 'Release year',
            yearInvalid: 'Write a year between 1950 and 2100, or leave it empty.',
            tags: 'Tags',
            tagsHint: 'HQ’s own labels, separated by commas. Never imported.',
            elevation: 'Needs administrator rights',
            elevationHint: 'Stations launch it through the elevated service, never as the player. A venue can turn this off for itself.',
            edited: 'Kept through refreshes',
            editedHint: 'These were changed by hand, so a refresh from IGDB leaves them alone. Tick one to hand it back to IGDB.',
            handBack: 'Hand {{field}} back to IGDB',
            field: {
                name: 'the name',
                summary: 'the description',
                genres: 'the genres',
                franchise: 'the series',
                firstReleaseYear: 'the release year',
                publisher: 'the publisher',
                ageRating: 'the age rating',
                tags: 'the tags',
            },
            cancel: 'Cancel',
            save: 'Save',
            create: 'Add game',
            saved: 'Saved {{game}}.',
            created: 'Added {{game}} to the catalogue.',
            failed: 'The game could not be saved.',
            archive: {
                title: 'Archive',
                intro: 'Hide it from venues adding titles. Venues that already offer it keep it, and their stations keep launching it.',
                start: 'Archive…',
                usageLoading: 'Counting the venues that offer it…',
                usageFailed: 'The venues that offer it could not be counted. You can still archive it.',
                usage_zero: 'No venue offers it.',
                usage_one: 'Offered by {{count}} venue. Its library entry keeps working.',
                usage_other: 'Offered by {{count}} venues. Their library entries keep working.',
                confirm: 'Archive game',
                done: 'Archived {{game}}.',
                isArchived: 'Archived on {{date}}. Venues cannot add it; those that offer it still do.',
                unarchive: 'Bring back',
                undone: '{{game}} is back in the catalogue.',
                failed: 'Could not change {{game}}.',
            },
        },
        // Detection testing (F-HQ-16): paste a station's process list, see which titles it would detect and why.
        detectionTest: {
            action: 'Test detection',
            title: 'Test detection',
            intro: 'See which titles a station would detect from its running processes.',
            label: 'Processes',
            hint: 'One per line: a name like cs2.exe or a full path. A tasklist /fo csv dump works. Full paths also test path patterns and regular expressions.',
            run: 'Test',
            empty: 'Paste at least one process.',
            failed: 'The list could not be tested.',
            summary_one: '{{count}} title matched across {{processes}} processes.',
            summary_other: '{{count}} titles matched across {{processes}} processes.',
            matchedNone: 'No title matched.',
            noneHint: 'Add process names under a title’s Detection, then test again.',
            caption: 'Detection results',
            noMatch: 'No title',
            matchDetail: 'by {{via}} ({{rules}})',
            via: {
                catalogue: 'its rules',
                recipe: 'its launch program',
                default: 'the suggested names',
            },
            rule: {
                processName: 'process name',
                pathPattern: 'path pattern',
                regex: 'regex',
            },
        },
        // Title requests (F-CAT-08): titles venues found on their PCs that the catalogue does not hold yet.
        requests: {
            title: 'Title requests',
            intro: 'Titles venues found installed on their PCs that the catalogue does not hold yet. Find one on IGDB, add it, then mark the request added.',
            empty: 'No title requests.',
            failed: 'The title requests could not be loaded.',
            caption: 'Title requests',
            count_one: 'asked {{count}} time',
            count_other: 'asked {{count}} times',
            lastAsked: 'last asked {{date}}',
            answeredBy: 'Answered by {{game}}.',
            status: {
                open: 'Open',
                added: 'Added',
                dismissed: 'Dismissed',
            },
            find: 'Find on IGDB',
            findLabel: 'Search IGDB for {{name}}',
            pickGame: 'Catalogue game',
            pickNone: 'Choose a game',
            markAdded: 'Mark added',
            dismiss: 'Dismiss',
            added: 'Marked the request for {{name}} added.',
            dismissed: 'Dismissed the request for {{name}}.',
            resolveFailed: 'The request could not be updated.',
        },
        // Apps: non-game titles HQ adds by hand — browsers, voice chat, launchers — which venues add to their
        // library and players open from the kiosk’s Apps screen.
        apps: {
            title: 'Apps',
            intro: 'Browsers, voice chat, music and the game launchers. Venues add them to their library; players open them from the kiosk’s Apps screen.',
            add: 'Add app',
            addCommon: 'Add common apps',
            addCommonLabel: 'Add the common apps that are not in the catalogue yet',
            addCommonDone_zero: 'Every common app is already in the catalogue.',
            addCommonDone_one: 'Added {{count}} common app.',
            addCommonDone_other: 'Added {{count}} common apps.',
            addCommonFailed: 'The common apps could not be added.',
            empty: 'No apps in the catalogue yet. Add one, or add the common apps.',
            caption: 'Apps in the catalogue',
            more: 'Show more apps',
            failed: 'The apps could not be loaded.',
            edit: 'Edit',
            editLabel: 'Edit {{app}}',
            iconAlt: '{{app}} icon',
            // The card's icon action: "Icon" when it has one, "Pick icon" when it does not, and their accessible names.
            icon: 'Icon',
            iconLabel: 'Icon for {{app}}',
            pickIcon: 'Pick icon',
            pickIconLabel: 'Pick icon for {{app}}',
            noIcon: 'No icon yet',
            // An app with a recipe a station can install it into the player account by.
            installsForPlayers: 'Installs for players',
            detectionCount_zero: 'No detection rules',
            detectionCount_one: 'Detected as {{names}}',
            detectionCount_other: 'Detected as {{names}}',
            category: {
                apps: 'Apps',
                browsers: 'Browsers',
                voice: 'Voice',
                media: 'Media',
                tools: 'Tools',
            },
            sheet: {
                createTitle: 'Add app',
                editTitle: 'Edit app',
                stepName: 'Name and category',
                stepLaunch: 'Launch',
                stepPlayers: 'For players',
                stepIcon: 'Icon',
                stepConfirm: 'Confirm',
                name: 'Name',
                nameHint: 'As players see it on the kiosk, for example Discord.',
                nameRequired: 'Write the app’s name.',
                summary: 'Description',
                category: 'Suggested kiosk category',
                categoryHint: 'Venues can still file it under another category.',
                launchIntro: 'How a station opens the app. Path variables like {ProgramFiles} and {UserProfile} are resolved on the PC.',
                processNames: 'Process names',
                processNamesHint: 'Separated by commas, for example discord, update. Case and .exe do not matter.',
                iconIntro: 'A square PNG or WebP, up to {{size}}. You can also pick one from SteamGridDB under Media once the app is added.',
                iconFile: 'Icon file',
                iconDrop: 'Choose a PNG or WebP',
                iconDropHint: 'Drag a file here, or click to choose. Up to {{size}}.',
                iconChosen: '{{name}} will be uploaded when the app is added.',
                iconNone: 'No icon chosen. The tile shows the app’s initials until one is set.',
                iconWrongType: 'An icon must be a PNG or WebP image.',
                iconTooLarge: 'That image is larger than {{size}}.',
                confirmIntro: 'Check the app before it is added to the catalogue.',
                noRecipes: 'No launch recipe',
                next: 'Next',
                back: 'Back',
                cancel: 'Cancel',
                create: 'Add app',
                save: 'Save',
                created: 'Added {{app}} to the catalogue.',
                saved: 'Saved {{app}}.',
                nameTaken: 'Another app already has that name.',
                failed: 'The app could not be saved.',
                iconFailed: '{{app}} was added, but its icon could not be uploaded. Try again under Media.',
            },
            // The "For players" step: how a PC installs an app that installs per Windows account into its player account,
            // when a venue turns "Install for players" on, and where the app keeps a player's sign-in.
            players: {
                intro: 'Some apps install for one Windows account only, so the account players use never has them. Give such an app a way for PCs to install it for players; each venue then decides whether its PCs do.',
                kind: 'How PCs install it for players',
                kindNone: 'They don’t',
                kindStore: 'Microsoft Store app',
                kindInstaller: 'Vendor installer',
                noneHint: 'Venues install it themselves. Pick this for apps that install for every account on the PC.',
                packageFamilyName: 'Package family name',
                packageFamilyNameHint: 'For example 5319275A.WhatsAppDesktop_cv1g1gvanyjgm. A PC registers the app for players when it is already installed for any account there; it never downloads it from the Store.',
                downloadUrl: 'Download link',
                downloadUrlHint: 'The vendor’s own https link to the latest installer.',
                allowedHosts: 'Allowed hosts',
                allowedHostsHint: 'Where the file may come from, including every redirect, for example discord.com, stable.dl2.discordapp.net.',
                signer: 'Signed by',
                signerHint: 'The publisher on the installer’s digital signature, exactly as written there, for example “Discord Inc.” with its full stop. A file signed by anyone else, or not signed, is refused.',
                silentArgs: 'Silent install arguments',
                silentArgsHint: 'For example -s or /silent.',
                timeoutSeconds: 'Time limit (seconds)',
                timeoutSecondsHint: 'From 30 to 1800. Blank: 600.',
                installRoot: 'Install folder',
                installRootHint: 'The app’s own folder, starting {LocalAppData} or {AppData}, for example {LocalAppData}\\Discord. PCs check it after every session and put back anything a player changed, so nobody can leave altered code for the next player.',
                restoreExclude: 'Data inside the install folder',
                restoreExcludeHint: 'Files and folders in it that hold settings or sign-ins, never put back, separated by commas, for example prefs, Users.',
                firstRunSeconds: 'First run (seconds)',
                firstRunSecondsHint: 'How long the app the installer opens may keep running to finish its own downloads, from 0 to 600. Blank: 0.',
                installedPath: 'Installed file',
                installedPathHint: 'The file that shows it is installed for the player, inside the install folder, for example {LocalAppData}\\Discord\\Update.exe.',
                closeProcesses: 'Close after installing',
                closeProcessesHint: 'Apps the installer starts by itself, separated by commas, for example discord.',
                removeRunValues: 'Stop starting with Windows',
                removeRunValuesHint: 'The names the app adds under Run, separated by commas, for example Discord.',
                expectedBlockedChildren: 'Blocked tools the app itself tries to run',
                expectedBlockedChildrenHint: 'These stay blocked while players use the app. Tick one the real app is known to try, so PCs note it quietly instead of raising a tamper alert. PCs check the app’s install folder and digital signature first; anything else that tries it is still an alert.',
                expectedChildTool: {
                    reg: 'Registry tool (reg.exe), for example Discord at every start',
                },
                signOut: 'Sign-in data',
                signOutHint: 'Folders and files where the app keeps a player’s sign-in. They are cleared after every session, so the next player starts signed out.',
                signOutRoot: 'Folder',
                signOutPath: 'Path',
                signOutKind: 'Clear',
                root: {
                    local_app_data: 'Local app data',
                    app_data: 'App data (roaming)',
                },
                kindDirectory: 'Everything in the folder',
                kindFile: 'The file',
                signOutAdd: 'Add a path',
                signOutRemove: 'Remove',
                signOutCount_one: '{{count}} sign-in path',
                signOutCount_other: '{{count}} sign-in paths',
                summary: {
                    none: 'Not installed by PCs',
                    store: 'Microsoft Store app',
                    installer: 'Vendor installer',
                },
                problem: {
                    packageFamilyName: 'Write the package family name, for example 5319275A.WhatsAppDesktop_cv1g1gvanyjgm.',
                    downloadUrl: 'Write the download link.',
                    notHttps: 'The download link must start with https://.',
                    allowedHosts: 'Write at least one allowed host.',
                    hostNotAllowed: 'The download link’s host must be one of the allowed hosts.',
                    signer: 'Write who signs the installer.',
                    installedPath: 'Write the file that shows the app is installed.',
                    perUserPath: 'The installed file must start with {LocalAppData}, {AppData} or {UserProfile}.',
                    timeout: 'The time limit must be a whole number from 30 to 1800.',
                    protectedProcess: 'One of the apps to close is part of Windows or Clavian. PCs never close those.',
                    installRoot: 'Write the install folder, starting {LocalAppData} or {AppData}, with at least one folder after it.',
                    broadInstallRoot: 'That folder holds other apps’ files. Name the app’s own folder inside it.',
                    outsideInstallRoot: 'The installed file must be inside the install folder.',
                    restoreExclude: 'A data path must stay inside the install folder: no drive, no leading slash, no .. and no variables.',
                    firstRun: 'The first run must be a whole number from 0 to 600.',
                    signOutPath: 'A sign-in path must stay inside its folder: no drive, no leading slash, no .. and no variables.',
                    server: 'Some of the details are not valid. Check the For players step.',
                },
            },
        },
        // The "Add games" surface: the search box and its results.
        add: {
            title: 'Add games',
            intro: 'Search the metadata provider (IGDB) for base games. Adding one mirrors it into the catalogue.',
            label: 'Search for a game',
            description: 'Part of a game’s name, for example half-life.',
            placeholder: 'Search IGDB, for example half-life',
            submit: 'Search',
            resultsTitle: 'From IGDB',
            clear: 'Clear search',
            tooShort: 'Write what to search for.',
            tooLong: 'Write at most {{count}} characters.',
            results: 'Search results',
            found_zero: 'No game matches “{{q}}”.',
            found_one: '{{count}} game matches “{{q}}”.',
            found_other: '{{count}} games match “{{q}}”.',
            none: 'No game matches “{{q}}”.',
            failed: 'The search could not be run.',
            // The 503 state: the provider’s Twitch credentials are not set (spec/14 §6).
            providerUnavailable: 'The game metadata provider is not configured, so searching and adding games are unavailable. It is set up in the deployment.',
            // The Add control on a result card, and its accessible name.
            button: 'Add',
            buttonLabel: 'Add {{game}} to the catalogue',
            adding: 'Adding {{game}}…',
            inCatalogue: 'In catalogue',
            // What a screen reader hears for the badge that replaces the Add button.
            inCatalogueFor: '{{game}} is in the catalogue.',
            // The 422 state, on the card: the pick is not a base game with a cover.
            notBaseGame: '{{game}} is an edition, a DLC or a bundle with no base game to add. Pick a base game.',
            addProviderUnavailable: 'The metadata provider is unavailable, so {{game}} could not be added.',
            addForbidden: 'Your HQ role cannot add games to the catalogue.',
            addFailed: '{{game}} could not be added.',
        },
        // The "In the catalogue" surface: every mirrored game, newest first.
        browse: {
            title: 'In the catalogue',
            intro: 'Every game in the catalogue, newest added first.',
            empty: 'No game in the catalogue yet. Search above to add the first one.',
            caption: 'Games in the catalogue',
            more: 'Show more games',
            failed: 'The catalogue could not be loaded.',
            // The Refresh control on a mirrored game card: re-read it from IGDB and re-cache its cover.
            refresh: 'Refresh',
            refreshLabel: 'Refresh {{game}} from the metadata provider',
            refreshFailed: '{{game}} could not be refreshed.',
            count_one: '{{count}} game',
            count_other: '{{count}} games',
        },
        // A cover card’s parts (a game’s box art at a 2:3 portrait aspect, its year and its franchise).
        card: {
            coverAlt: '{{game}} cover art',
            year: 'Released {{year}}',
            yearUnknown: 'Release year unknown',
            franchise: '{{franchise}} series',
            noCover: 'No cover yet',
            // What a card opens, shown over its art on hover and focus.
            openEdit: 'Edit',
            openMedia: 'Media',
            // A game's kiosk media at a glance: whether a hero and a logo are set.
            hero: 'Hero',
            noHero: 'No hero',
            logo: 'Logo',
            noLogo: 'No logo',
            archived: 'Archived',
            admin: 'Needs admin',
            byHand: 'Added by hand',
        },
        // Launch recipes (F-CAT-05, spec/10 §5.2): how a station launches a game. HQ curates one or two
        // per launcher; the venue library carries them to the station.
        launch: {
            action: 'Launch recipes',
            actionCount_one: '{{count}} launch recipe',
            actionCount_other: '{{count}} launch recipes',
            actionLabel: 'Edit how a station launches {{game}}',
            title: 'Launch recipes',
            intro: 'How a station launches this game. Add one recipe per launcher; the venue library carries them to the station.',
            empty: 'No launch recipes yet. Add one so a station knows how to launch this game.',
            add: 'Add a recipe',
            remove: 'Remove',
            cancel: 'Cancel',
            save: 'Save recipes',
            saved: 'Saved launch recipes for {{game}}.',
            failed: 'The launch recipes could not be saved.',
            incomplete: 'Fill in each recipe’s required field, or remove it.',
            launcher: 'Launcher',
            type: 'Launch by',
            label: 'Label',
            path: 'Executable path',
            lnkPath: 'Shortcut path',
            pathHint: 'Path variables like {ProgramFiles} and {SteamLibrary} are resolved on the PC.',
            args: 'Arguments',
            workingDirectory: 'Working directory',
            uri: 'Launcher URI',
            uriHint: 'For example steam://rungameid/730 or com.epicgames.launcher://apps/…?action=launch.',
            aumid: 'Store AUMID',
            aumidHint: 'The full Microsoft Store application user model id.',
            launcherName: {
                steam: 'Steam',
                epic: 'Epic Games',
                ea: 'EA app',
                ubisoft: 'Ubisoft Connect',
                battlenet: 'Battle.net',
                riot: 'Riot Client',
                microsoft_store: 'Microsoft Store',
                standalone: 'Standalone',
                other: 'Other',
            },
            typeName: {
                exe: 'Executable',
                uri: 'Launcher URI',
                store: 'Microsoft Store',
                lnk: 'Shortcut',
            },
        },
        // Detection (F-CAT-04, spec/10 §7.3): how a station recognises a title's process, so play is tracked.
        // For a game it is the game's own process, never its launcher's.
        detection: {
            action: 'Detection',
            actionLabel: 'Edit how a station detects {{game}}',
            title: 'Detection',
            intro: 'How a station recognises that this game is being played. Every rule set here must match.',
            processNames: 'Process names',
            processNamesHint: 'The game’s own .exe, for example dota2.exe — not the launcher. Separate several with commas.',
            processNamesPreview: 'Detected as',
            pathPattern: 'Path pattern',
            pathPatternHint: 'A wildcard over the program’s path, for example {SteamLibrary}\\*\\dota2.exe.',
            regex: 'Regular expression',
            regexHint: 'Case-insensitive, over the program’s path. Narrows the process names.',
            none: 'No detection rules yet. A station cannot tell when this game is played.',
            suggested: 'Use suggested: {{names}}',
            suggestedHint: 'The usual process for {{game}}. Review it, then save.',
            cancel: 'Cancel',
            save: 'Save detection',
            saved: 'Saved detection for {{game}}.',
            failed: 'The detection rules could not be saved.',
        },
        // Game media (spec/14 §6, ADR-0028): the wide hero art, the logo and the trailers the kiosk shows. HQ
        // picks art from SteamGridDB or IGDB; the server copies it to our media CDN and stations cache it.
        media: {
            action: 'Media',
            actionLabel: 'Media for {{game}}',
            title: 'Media',
            intro: 'The hero art, logo and trailers stations show for this game. Picks are copied to our media CDN and cached on every station.',
            tabs: 'Media kind',
            hero: 'Hero',
            logo: 'Logo',
            icon: 'Icon',
            iconIntro: 'The square tile players see on the kiosk’s Apps screen. Upload a PNG or WebP, or pick one from SteamGridDB.',
            iconUpload: {
                file: 'Upload an icon',
                drop: 'Upload PNG or WebP',
                hint: 'Drag a file here, or click to choose. Up to {{size}}, square works best.',
                uploading: 'Uploading {{name}}',
                uploaded: 'Icon uploaded for {{game}}.',
                wrongType: 'An icon must be a PNG or WebP image.',
                tooLarge: 'That image is larger than {{size}}.',
                failed: 'The icon could not be uploaded. Try again.',
            },
            trailers: 'Trailers',
            current: 'Current',
            none: 'None picked yet',
            noneHint: 'Upload one, or pick a candidate below.',
            retry: 'Try again',
            candidateCount_one: '{{count}} candidate',
            candidateCount_other: '{{count}} candidates',
            currentAlt: 'Current {{kind}} for {{game}}',
            candidates: 'Candidates',
            candidatesFor: '{{kind}} candidates for {{game}}',
            candidateAlt: '{{source}} {{kind}} {{index}}',
            pick: 'Use this',
            pickLabel: 'Use {{source}} {{kind}} {{index}}',
            picked: 'In use',
            clear: 'Clear',
            clearLabel: 'Clear the {{kind}}',
            loading: 'Loading candidates…',
            noCandidates: 'No candidates from the configured sources.',
            unavailable: '{{sources}} is not configured or cannot answer right now.',
            allUnavailable: 'No art source is configured or can answer. Set STEAMGRIDDB_API_KEY (and the Twitch credentials for IGDB).',
            failed: 'The candidates could not be loaded.',
            pickFailed: 'That image could not be used. Try another.',
            saved: '{{kind}} updated for {{game}}.',
            cleared: '{{kind}} cleared for {{game}}.',
            size: '{{width}} × {{height}}',
            source: {
                steamgriddb: 'SteamGridDB',
                igdb: 'IGDB',
                upload: 'Uploaded',
            },
            trailer: {
                intro: 'MP4 or WebM, up to {{size}}. The kiosk’s lock screen plays every ready trailer switched into the kiosk loop, muted and in turn — up to five per game, newest first.',
                titleLabel: 'Title',
                titleDefault: 'Trailer',
                file: 'Video file',
                drop: 'Choose an MP4 or WebM video',
                dropHint: 'Drag a file here, or click to choose. Up to {{size}}.',
                upload: 'Upload',
                uploading: 'Uploading {{name}}',
                uploaded: 'Trailer uploaded for {{game}}.',
                wrongType: 'A trailer must be an MP4 or WebM video.',
                tooLarge: 'That video is larger than {{size}}.',
                uploadFailed: 'The upload failed. Try again.',
                empty: 'No trailers yet.',
                list: 'Trailers for {{game}}',
                remove: 'Remove',
                removeLabel: 'Remove trailer {{title}}',
                removeFailed: 'The trailer could not be removed.',
                loop: 'Kiosk loop',
                loopLabel: 'Play {{title}} in the kiosk loop',
                loopFailed: 'The kiosk loop could not be changed.',
                status: {
                    pending: 'Uploading',
                    ready: 'Ready',
                    failed: 'Failed',
                },
                failedToLoad: 'The trailers could not be loaded.',
                megabytes: '{{value}} MB',
            },
        },
    },
    // The provider status page (F-HQ-38, spec/14 §15): the external services the deployment uses, and a safe check.
    providers: {
        title: 'Providers',
        intro: 'The outside services this deployment uses, whether each is set up, and the last check. Test proves a provider answers without sending anything or moving money. Secrets are never shown.',
        caption: 'External providers',
        unavailable: 'Could not load the providers.',
        none: 'Not set up',
        neverChecked: 'Not checked yet',
        notTestable: 'No safe check — it would send a message or move money',
        latency: '{{ms}} ms',
        column: {
            provider: 'Provider',
            status: 'Status',
            configuration: 'Configuration',
            lastCheck: 'Last check',
            actions: 'Actions',
        },
        status: {
            not_configured: 'Not set up',
            configured: 'Set up',
            reachable: 'Working',
            unreachable: 'Not working',
        },
        test: {
            action: 'Test',
            running: 'Testing…',
            label: 'Test {{name}}',
            passed: '{{name}} is working.',
            failed: '{{name}} did not answer. The error is on its row.',
            error: 'Could not test {{name}}.',
        },
    },
    settings: {
        title: 'Platform settings',
        sections: 'Platform settings sections',
        defaults: 'Platform defaults',
        brand: 'Brand',
        providers: 'Providers',
        organization: 'One organisation',
        open: {
            label: 'Organisation id',
            submit: 'Open its settings',
            invalid: 'Write the organisation’s id: org_ followed by 26 letters and digits.',
        },
        organizationTitle: 'The settings of organisation',
        audited: 'Head office reading these settings is recorded in the organisation’s audit log.',
        noSuchOrganization: 'No organisation has this id.',
        // Why a setting on an HQ screen cannot be set there.
        notHere: {
            platform: 'Not set for the whole platform: each organisation or venue sets it on its own screen.',
            organization: 'Not set per organisation: it is a platform default, set under Platform defaults.',
        },
        // Where a value comes from, on HQ's screens.
        source: {
            default: 'Default',
            platform: 'Platform default',
            organization: 'Set for this organisation',
            venue: 'Set for a venue',
        },
        sourceName: {
            default: 'the default',
            platform: 'the platform default',
            organization: 'this organisation',
            venue: 'the venue',
        },
        // Each row's source line on HQ's screens (the venue console's words, from head office's side).
        using: {
            default: 'Using the default — {{value}}',
            platform: 'Using the platform default — {{value}}',
            organization: 'Set for this organisation — {{value}}',
            venue: 'Set for a venue — {{value}}',
        },
        changed: {
            platform: 'Set for the whole platform',
            organization: 'Set for this organisation',
        },
        resetTo: {
            default: 'Reset to the default {{value}}',
            platform: 'Reset to the platform default {{value}}',
            organization: 'Reset to this organisation’s {{value}}',
            venue: 'Reset to the venue’s {{value}}',
        },
    },
    brand: {
        preview: {
            title: 'Preview',
            hint: 'The saved brand on a venue’s lock screen. Save a change to see it here.',
            eyebrow: 'PC 12 · Ready',
            heading: 'Sign in to play',
            body: 'Scan the code with the app, or sign in with your phone number.',
            button: 'Start a session',
            link: 'Need help? Call staff',
            surface: { dark: 'Dark surface', light: 'Light surface' },
        },
        title: 'Brand',
        intro: 'The brand values head office sets while the platform runs. A group you set replaces the deployment’s; a reset hands it back. ' +
            'The product name, the domains, the apps and the email sender are the deployment’s, and change with a deploy.',
        revision: 'Revision {{revision}}, saved {{when}}.',
        revisionNone: 'Head office has set nothing yet: every group is the deployment’s.',
        unavailable: 'The brand could not be loaded.',
        source: {
            platform: 'Set by head office',
            default: 'The deployment’s',
        },
        sourceName: {
            platform: 'head office',
            default: 'the deployment',
        },
        // The five groups (spec/14 §14), by id.
        group: {
            support: 'Support contacts',
            legal: 'Legal documents',
            theme: 'Design tokens',
            kioskScreensaver: 'Kiosk default media',
            organizationBranding: 'Organisation branding',
        },
        // A property inside a group, by name.
        field: {
            email: 'Support email',
            phone: 'Support phone',
            terms: 'Terms of service',
            privacy: 'Privacy policy',
            refund: 'Refund policy',
            version: 'Version in force',
            url: 'Address',
            copy: 'The Operator’s own wording',
            colour: 'Colours',
            primary: 'Primary colour',
            accent: 'Accent colour',
            surface: 'Surface',
            radius: 'Corners',
            font: 'Fonts',
            display: 'Headings',
            body: 'Body text',
            media: 'Attract media',
            colours: 'Organisations may use their own colours',
            logo: 'Organisations may add their own logo',
        },
        item: {
            media: 'Media file',
        },
        description: {
            phone: 'With the country code, for example +91 98765 43210.',
            version: 'The version a sign-up must have been shown.',
            url: 'An https address.',
            copy: 'By language: the cookie notice and the consent wording, where the Operator’s must replace the platform’s.',
            primary: 'Buttons, marks and large numbers. #RRGGBB, at least 3:1 against the surface.',
            accent: 'Text, links and the focus ring. #RRGGBB, at least 4.5:1 against the surface.',
            surface: 'The ground the brand’s screens stand on.',
            media: 'Images and videos under brand/ in public media, for a venue that has uploaded none.',
            colours: 'On their venues’ kiosk lock screens and receipts.',
            logo: 'On their venues’ kiosk lock screens and receipts.',
        },
        option: {
            surface: {
                dark: 'Dark',
                light: 'Light',
            },
            radius: {
                sharp: 'Sharp',
                soft: 'Soft',
                round: 'Round',
            },
        },
    },
    // Players (spec/14 §10): looking a person up across the Platform, their platform record, and the
    // platform ban. Contact details are shown in full to HQ (the product owner, 2026-09-22).
    players: {
        title: 'Players',
        intro: 'Look a person up across the Platform. You see their platform record: never an organisation’s dealings with them.',
        audited: 'Each search and each player you open is recorded in the platform’s audit log.',
        banned: 'Banned',
        notBanned: 'None',
        notGiven: 'Not given',
        search: {
            name: 'Search the Platform’s players',
            label: 'Phone, email, gamer tag, name or player id',
            description: 'A phone number with its country code (+91…) or an email address matches only in full, and a player id (ply_…) that one player. ' +
                'Anything else matches part of a name, a display name or a gamer tag.',
            submit: 'Search',
            tooShort: 'Write at least {{count}} characters.',
            tooLong: 'Write at most {{count}} characters.',
            results: 'Results',
            found_zero: 'No player matches “{{q}}”.',
            found_one: '{{count}} player matches “{{q}}”.',
            found_other: '{{count}} players match “{{q}}”.',
            foundMore_one: '{{count}} player shown. More match: show them below.',
            foundMore_other: '{{count}} players shown. More match: show them below.',
            caption: 'Players matching “{{q}}”, oldest account first',
            none: 'No player matches “{{q}}”.',
            more: 'Show more players',
            failed: 'The search could not be run.',
        },
        column: {
            player: 'Player',
            phone: 'Phone',
            email: 'Email',
            ban: 'Platform ban',
            joined: 'Account made',
        },
        record: {
            back: 'Back to the search',
            noSuchPlayer: 'No player has this id.',
            unavailable: 'This player could not be loaded.',
            audited: 'Opening this record is recorded in the platform’s audit log.',
            notGivenList: 'Not given by the player: {{what}}',
            profile: 'Profile',
            name: 'Name',
            displayName: 'Display name',
            username: 'Gamer tag',
            id: 'Player id',
            createdAt: 'Account made',
            contact: 'Contact',
            phone: 'Phone',
            email: 'Email',
            verified: 'Verified',
            unverified: 'Not verified',
            signingIn: 'Signing in',
            methods: 'Sign-in methods',
            secondFactor: 'Second factor',
            secondFactorOn: 'On',
            secondFactorOff: 'Off',
        },
        // spec/07 §3.1, by `HqPlayerSignInMethod`.
        method: {
            phone: 'Phone code',
            email: 'Email code',
            password: 'Password',
            google: 'Google',
            discord: 'Discord',
            steam: 'Steam',
        },
        devices: {
            title: 'Devices',
            caption: 'The devices they signed in on most recently, newest first',
            device: 'Device',
            firstSeen: 'First seen',
            lastSeen: 'Last seen',
            none: 'They have not signed in on any device yet.',
        },
        // The platform ban (spec/07 §3.5): `hq:players`, an HQ owner's alone.
        ban: {
            title: 'Platform ban',
            none: 'Not banned from the Platform.',
            inForce: 'They cannot sign in anywhere on the Platform.',
            lapsedBadge: 'Expired',
            lapsed: 'A platform ban is on record, but it has expired and stops nothing.',
            reason: 'Reason',
            until: 'Until',
            untilLifted: 'Until it is lifted',
            internal: 'The reason is head office’s alone. The player, venues and organisations never see it.',
            reasonRequired: 'Write why.',
            forbidden: 'Refused. Only an HQ owner bans from the Platform, and nobody can ban their own account.',
            nothingThere: 'There is no platform ban on record to lift.',
            failed: 'The ban could not be saved.',
            action: {
                ban: 'Ban from the Platform',
                change: 'Change the ban',
                lift: 'Lift the ban',
            },
            saved: {
                banned: 'Banned from the Platform. They are signed out everywhere.',
                changed: 'The ban is changed.',
                lifted: 'The ban is lifted. They can sign in again.',
            },
            dialog: {
                banTitle: 'Ban {{name}} from the Platform',
                banConsequence: 'They will not be able to sign in anywhere, on the web, in the apps or at a kiosk, and they are signed out everywhere now.',
                changeTitle: 'Change {{name}}’s platform ban',
                changeConsequence: 'The ban stays in force, with this reason and length.',
                liftTitle: 'Lift {{name}}’s platform ban',
                liftConsequence: 'They can sign in again. Where the ban signed them out, they stay signed out until they sign in.',
                reason: 'Reason',
                reasonDescription: 'Why, for head office and the audit log. Never shown to the player.',
                liftReasonDescription: 'Why the ban is lifted, for head office and the audit log.',
                length: 'How long',
                keep: 'Keep its end, {{when}}',
                lengths: {
                    lifted: 'Until it is lifted',
                    '1': '24 hours from now',
                    '7': '7 days from now',
                    '30': '30 days from now',
                    '90': '90 days from now',
                },
                cancel: 'Cancel',
                submitBan: 'Ban from the Platform',
                submitChange: 'Save the ban',
                submitLift: 'Lift the ban',
            },
        },
    },
    // The platform audit log (spec/14 §15, spec/07 §11.2, #395): every organisation's log and the
    // platform's, read with a reason that every organisation shown sees.
    // Fleet: station releases (spec/14 §12, F-STN-12, spec/10 §8): the signed releases HQ registers, each
    // channel's staged rollout, and the client versions across the platform's stations.
    releases: {
        title: 'Station releases',
        intro: 'The station client releases HQ has registered, how far each channel rolls one out, and what the fleet runs.',
        failed: 'The station releases could not be loaded.',
        halted: 'Halted',
        active: 'Active',
        channel: {
            stable: 'Stable',
            beta: 'Beta',
        },
        list: {
            title: 'Releases',
            intro: 'Newest first. A halted release is offered to no station; stations already on it stay on it.',
            caption: 'Registered station releases',
            empty: 'No release registered yet. Register the first one from the release tool’s signed manifest.',
            column: {
                version: 'Version',
                channel: 'Channel',
                released: 'Released',
                kiosk: 'Kiosk',
                size: 'MSI size',
                status: 'Status',
                actions: 'Actions',
            },
            halt: 'Halt',
            resume: 'Resume',
            haltLabel: 'Halt release {{version}}',
            resumeLabel: 'Resume release {{version}}',
            halted: 'Release {{version}} is halted.',
            resumed: 'Release {{version}} is offered again.',
            changeFailed: 'Release {{version}} could not be changed.',
        },
        register: {
            action: 'Register release',
            title: 'Register a release',
            description: 'Paste the signed manifest the release tool printed. The server checks its signature before storing it; registering offers it to no station until a rollout targets it.',
            manifest: 'Signed manifest',
            manifestHint: 'A compact JWS: three base64url parts joined by dots.',
            required: 'Paste the signed manifest.',
            malformed: 'This is not a signed manifest: it should be three base64url parts joined by dots.',
            submit: 'Register release',
            cancel: 'Cancel',
            conflict: 'A release with this version is already registered.',
            unprocessable: 'The server refused this manifest: the signature does not verify, no release keys are configured, or the manifest is malformed.',
            failedGeneric: 'The release could not be registered.',
            done: 'Release {{version}} is registered.',
        },
        rollouts: {
            title: 'Rollouts',
            intro: 'Each channel offers one release to a share of its stations. Raising the share only adds stations; the choice of stations is fixed per version.',
            failed: 'The rollouts could not be loaded.',
            cardTitle: '{{channel}} channel',
            summary: '{{version}} to {{percentage}}% of stations',
            summaryNone: 'No target: nothing is offered.',
            updated: 'Changed {{when}}',
            neverSet: 'Never set',
            target: 'Target version',
            targetHint: 'Registered releases that are not halted.',
            noTarget: 'No target (offer nothing)',
            haltedOption: '{{version}} (halted)',
            percentage: 'Share of stations',
            percentageHint: 'The percentage of this channel’s stations offered the target.',
            percentageNumber: 'Share of stations, in percent',
            percentageValue: '{{value}}%',
            save: 'Save rollout',
            saveLabel: 'Save the {{channel}} rollout',
            noChanges: 'No changes to save.',
            halt: 'Halt rollout',
            resume: 'Resume rollout',
            haltLabel: 'Halt the {{channel}} rollout',
            resumeLabel: 'Resume the {{channel}} rollout',
            haltedNote: 'Halted: this channel offers nothing. Stations already updated stay updated.',
            saved: 'The {{channel}} rollout is saved.',
            haltedDone: 'The {{channel}} rollout is halted.',
            resumedDone: 'The {{channel}} rollout is resumed.',
            unregistered: 'That version is not a registered release.',
            saveFailed: 'The rollout could not be saved.',
            confirm: {
                title: 'Roll out {{version}} on {{channel}}?',
                description: '{{percentage}}% of the {{channel}} channel’s stations will download and install {{version}} when they are next idle.',
                submit: 'Roll it out',
                cancel: 'Cancel',
            },
        },
        fleet: {
            title: 'Versions in use',
            intro: 'How many stations run each client version, and what their updaters are doing. Counts only.',
            failed: 'The fleet could not be loaded.',
            reporting_one: '{{count}} station reporting',
            reporting_other: '{{count}} stations reporting',
            byVersion: 'By client version',
            byState: 'By updater state',
            none: 'No station has reported a version yet.',
            row_one: '{{label}}: {{count}} station ({{share}})',
            row_other: '{{label}}: {{count}} stations ({{share}})',
            state: {
                idle: 'Idle',
                downloading: 'Downloading',
                downloaded: 'Ready to install',
                installing: 'Installing',
                rolled_back: 'Rolled back',
                failed: 'Failed',
            },
        },
        boot: {
            title: 'Boot server releases',
            intro: 'Releases of the Clavian Boot server software. Boot servers on a channel download the release current on it and check it, then wait: it is installed only when the venue chooses Update now.',
            failed: 'The boot server releases could not be loaded.',
            caption: 'Registered boot server releases',
            empty: 'No boot server release registered yet. Register the first one from the release tool’s signed manifest.',
            channelsLabel: 'What each channel offers',
            channelCurrent: '{{channel}}: {{version}}',
            channelNone: '{{channel}}: nothing offered',
            notCurrent: 'Not offered',
            makeCurrent: 'Make current',
            makeCurrentLabel: 'Make boot server release {{version}} current',
            stop: 'Stop offering',
            stopLabel: 'Stop offering a boot server release on {{channel}}',
            column: {
                version: 'Version',
                channel: 'Cut for',
                released: 'Released',
                size: 'MSI size',
                current: 'Current on',
                actions: 'Actions',
            },
            current: {
                title: 'Offer {{version}} to boot servers?',
                description: 'Boot servers on the channel download it at their next check and show it as an update. Nothing is installed until the venue chooses Update now.',
                channel: 'Channel',
                submit: 'Make current',
                cancel: 'Cancel',
                done: '{{version}} is now offered on {{channel}}.',
                stopped: 'Nothing is offered on {{channel}} now.',
                unregistered: 'That version is not a registered boot server release.',
                failed: 'The channel could not be changed.',
            },
            register: {
                action: 'Register boot server release',
                title: 'Register a boot server release',
                description: 'Paste the signed manifest the boot server release tool printed. The server checks its signature before storing it; registering offers it to no boot server until you make it current.',
                manifest: 'Signed manifest',
                manifestHint: 'A compact JWS: three base64url parts joined by dots.',
                required: 'Paste the signed manifest.',
                malformed: 'This is not a signed manifest: it should be three base64url parts joined by dots.',
                submit: 'Register release',
                cancel: 'Cancel',
                conflict: 'A boot server release with this version is already registered.',
                unprocessable: 'The server refused this manifest: the signature does not verify, no release keys are configured, or it is not a boot server release.',
                failedGeneric: 'The release could not be registered.',
                done: 'Boot server release {{version}} is registered.',
            },
        },
    },
    audit: {
        title: 'Audit log',
        intro: 'Every recorded action on the Platform: who did it, where, and when.',
        platform: 'Platform',
        none: 'None',
        reason: {
            title: 'Why are you reading the audit log?',
            label: 'Reason',
            description: 'Required before anything is shown. It is recorded with your name in the platform’s log, and in the audit log of every ' +
                'organisation whose rows you are shown: its owner sees that head office looked, and why.',
            submit: 'Show the audit log',
            tooShort: 'Write at least {{count}} characters.',
            inUse: 'Your reason for reading',
            seenBy: 'Every organisation whose rows appear below sees this reason and your name in its own audit log. It is sent with every read ' +
                'and export on this page until you change it or leave the page.',
            seenByName: 'Every organisation whose rows appear below sees this reason, and that {{name}} read them, in its own audit log. It is sent ' +
                'with every read and export on this page until you change it or leave the page.',
            change: 'Change the reason',
        },
        trace: {
            title: 'Trace a request',
            label: 'Request id',
            description: 'Paste the req_… a caller quotes to see every row that request wrote, and nothing else.',
            submit: 'Find its rows',
            invalid: 'Write the request id: req_ followed by 26 letters and digits.',
            heading: 'Every row one request wrote',
            banner: 'Every other filter is set aside while you trace a request.',
            requestId: 'Request id',
            traceId: 'Trace id',
            open: 'Open the trace (in a new tab)',
            clear: 'Back to the whole log',
        },
        filters: {
            title: 'Filters',
            bar: 'Filter the audit log',
            search: 'Search the log',
            searchPlaceholder: 'Action, or a req_, org_ or ven_ id',
            searchInvalid: 'Search by an action, such as sessions.comp or sessions, or paste a req_, org_ or ven_ id.',
            organizationId: 'Organisation id',
            venueId: 'Venue id',
            action: 'Action',
            actionDescription: 'An action such as sessions.comp, or every action under a prefix: sessions.*',
            actorKind: 'Kind of actor',
            anyActor: 'Anyone',
            actorId: 'Actor id',
            targetType: 'Target type',
            targetTypeDescription: 'The kind of thing the row is about, such as player or setting_value.',
            targetId: 'Target id',
            onBehalfOf: 'Support grant or impersonation',
            onBehalfOfDescription: 'Only what was done under this break-glass grant or impersonation.',
            from: 'From (UTC)',
            fromDescription: 'Rows that happened at or after this time, in UTC.',
            to: 'Before (UTC)',
            toDescription: 'Rows that happened before this time, in UTC.',
            apply: 'Apply filters',
            clear: 'Clear filters',
            clearAll: 'Clear all',
            more: 'More filters',
            moreDescription: 'Narrow the log by where, who, what and when, or trace one request.',
            groups: {
                where: 'Where',
                who: 'Who and what',
                when: 'When',
            },
            range: {
                label: 'Time range',
                day: '24 h',
                week: '7 d',
                month: '30 d',
                all: 'All time',
                custom: 'Custom',
            },
            // A removable chip for each filter in use.
            inUse: 'Filters in use',
            chip: {
                organizationId: 'Organisation: {{value}}',
                venueId: 'Venue: {{value}}',
                action: 'Action: {{value}}',
                actorKind: 'Who: {{value}}',
                actorId: 'Actor: {{value}}',
                targetType: 'About: {{value}}',
                targetId: 'Target: {{value}}',
                onBehalfOf: 'Grant: {{value}}',
                from: 'From {{value}}',
                to: 'Before {{value}}',
                day: 'Last 24 hours',
                week: 'Last 7 days',
                month: 'Last 30 days',
            },
            invalidOrganization: 'Write the organisation’s id: org_ followed by 26 letters and digits.',
            invalidVenue: 'Write the venue’s id: ven_ followed by 26 letters and digits.',
            invalidInstant: 'Write a date and time.',
            window: 'Make this later than From.',
        },
        // `AuditActor.kind`.
        actorKind: {
            user: 'Person',
            member: 'Staff member',
            device: 'Device',
            api_key: 'API key',
            system: 'The system',
        },
        // The kind, short, as a badge beside the actor's name.
        actorKindShort: {
            user: 'Person',
            member: 'Staff',
            device: 'Device',
            api_key: 'API key',
            system: 'System',
        },
        onBehalfOf: {
            support_grant: 'Under support grant',
            impersonation: 'Under impersonation',
        },
        onBehalfOfShort: {
            support_grant: 'Support grant',
            impersonation: 'Impersonation',
        },
        column: {
            when: 'When',
            action: 'Action',
            actor: 'Who',
            where: 'Where',
            target: 'About',
        },
        rows: {
            title: 'Rows',
            caption: 'Audit rows, newest first',
            captionTrace: 'The rows request {{id}} wrote, newest first',
            loading: 'Reading the audit log',
            shown_one: '{{count}} row, newest first',
            shown_other: '{{count}} rows, newest first',
            shownMore_one: '{{count}} row so far, newest first',
            shownMore_other: '{{count}} rows so far, newest first',
            empty: 'No rows match these filters',
            emptyDescription: 'Widen the time range or remove a filter.',
            emptyLog: 'Nothing has been recorded yet',
            emptyTrace: 'That request wrote no audit rows.',
            more: 'Show older rows',
            refused: 'The filters were refused:',
            failed: 'The audit log could not be read.',
        },
        // One row, opened.
        detail: {
            eyebrow: 'Audit row',
            when: 'When',
            whenLocal: 'Your time',
            whenUtc: 'UTC',
            recordedAt: 'Recorded',
            who: 'Who',
            actorId: 'Actor id',
            actorIp: 'IP address',
            onBehalfOf: 'Acting under',
            where: 'Where',
            organization: 'Organisation',
            venue: 'Venue',
            about: 'About',
            target: 'Target',
            reason: 'Reason given',
            request: 'Request',
            requestId: 'Request id',
            traceId: 'Trace id',
            rowId: 'Row id',
            noRequest: 'Written outside a request, by a job or the system.',
            showTrace: 'Show everything this request did',
            copy: 'Copy',
            copyNamed: 'Copy the {{name}}',
            copied: 'Copied.',
            names: {
                actorId: 'actor id',
                onBehalfOf: 'grant id',
                organizationId: 'organisation id',
                venueId: 'venue id',
                targetId: 'target id',
                requestId: 'request id',
                traceId: 'trace id',
                rowId: 'row id',
            },
        },
        changes: {
            summary: 'What changed',
            caption: 'What changed, field by field',
            field: 'Field',
            before: 'Before',
            after: 'After',
            nothing: 'No change was recorded with this row.',
        },
        export: {
            submit: 'Export as CSV',
            done: 'Saved {{file}}.',
            tooMany: 'More than {{max}} rows match. An export is never cut short: narrow the filters, by time, organisation, actor or action, and export again.',
            failed: 'The export could not be made.',
        },
        // What a row's action reads as, by its code with the dots written as `__`. A code with no copy here
        // reads as its own words: `shifts.shift.closed` → "Shift closed".
        actions: {
            access__invitation__accept: 'Invitation accepted',
            access__invitation__create: 'Invitation sent',
            access__invitation__revoke: 'Invitation withdrawn',
            access__member__remove: 'Member removed',
            access__member__role__update: 'Member’s role changed',
            access__member__status__update: 'Member’s status changed',
            access__member__venues__update: 'Member’s venues changed',
            access__player__ban: 'Player banned',
            access__player__ban__update: 'Player’s ban changed',
            access__player__unban: 'Player’s ban lifted',
            access__role__create: 'Role created',
            access__role__delete: 'Role deleted',
            access__role__update: 'Role changed',
            access__role__limits__update: 'Role’s limits changed',
            access__shared_device__lock: 'Shared device locked',
            access__shared_device__open: 'Shared device opened',
            access__shared_device__unlock: 'Shared device unlocked',
            access__staff_pin__lock: 'Staff PIN locked',
            access__staff_pin__reset: 'Staff PIN reset',
            access__staff_pin__set: 'Staff PIN set',
            audit__log__export: 'Audit log exported',
            audit__log__read: 'Audit log read',
            catalog__game__launch_recipes__set: 'Launch recipes set',
            catalog__game__refresh: 'Game refreshed from IGDB',
            catalog__title__publish: 'Game published',
            commerce__credit__limit_set: 'Credit limit set',
            commerce__credit__settled: 'Account settled',
            commerce__credit__written_off: 'Balance written off',
            commerce__promotion__created: 'Promotion created',
            commerce__promotion__updated: 'Promotion changed',
            commerce__sale__cancelled: 'Sale cancelled',
            commerce__sale__discount_applied: 'Discount applied',
            commerce__sale__refunded: 'Sale refunded',
            hq__addon__create: 'Add-on priced',
            hq__addon__update: 'Add-on changed',
            hq__organization__approve: 'Organisation approved',
            hq__organization__create: 'Organisation registered',
            hq__organization__decline: 'Organisation declined',
            hq__organization_addon__attach: 'Add-on attached',
            hq__organization_addon__cancel: 'Add-on cancelled',
            hq__organization_addon__quantity: 'Add-on PCs changed',
            identity__user__ban: 'Platform ban set',
            identity__user__ban__update: 'Platform ban changed',
            identity__user__platform_role__grant: 'Platform role granted',
            identity__user__platform_role__revoke: 'Platform role removed',
            identity__user__read: 'Person’s record read',
            identity__user__search: 'People searched',
            identity__user__unban: 'Platform ban lifted',
            orders__discount: 'Order discounted',
            orders__refund: 'Order refunded',
            org__brand__update: 'Brand changed',
            org__setting__update: 'Setting changed',
            org__settings__read: 'Settings read',
            org__venue__archive: 'Venue archived',
            org__venue__create: 'Venue created',
            org__venue__listing: 'Venue’s listing changed',
            org__venue__update: 'Venue changed',
            org__zone__create: 'Zone created',
            org__zone__update: 'Zone changed',
            passes__holding__refunded: 'Pass refunded',
            passes__holding__voided: 'Pass voided',
            players__id_check__view: 'Player’s ID check viewed',
            players__note__add: 'Note added to a player',
            players__note__edit: 'Player’s note edited',
            players__note__remove: 'Player’s note removed',
            players__tags__update: 'Player’s tags changed',
            players__update: 'Player’s record changed',
            players__ban: 'Player banned',
            pricing__rate_card__update: 'Rate card changed',
            sessions__comp: 'Time comped',
            franchise__list: 'Franchises viewed',
            franchise__venues__read: 'Franchise venues viewed',
            kitchen__ticket__advanced: 'Kitchen ticket moved on',
            sessions__call__acknowledged: 'Call for help acknowledged',
            sessions__call__closed: 'Call for help closed',
            sessions__call__opened: 'Call for help raised',
            sessions__call__resolved: 'Call for help resolved',
            sessions__call__updated: 'Call for help updated',
            sessions__charged: 'Session charged',
            sessions__ended: 'Session ended',
            sessions__lock: 'Screen locked',
            sessions__unlock: 'Screen unlocked',
            sessions__message: 'Message sent to the player',
            sessions__sell_time: 'Time sold',
            sessions__session__comped: 'Time comped',
            sessions__session__ended: 'Session ended',
            sessions__session__locked: 'Screen locked',
            sessions__session__unlocked: 'Screen unlocked',
            sessions__session__messaged: 'Message sent to the player',
            sessions__session__sold_time: 'Time sold',
            sessions__session__started: 'Session started',
            sessions__session__warned: 'Low-time warning sent',
            sessions__start: 'Session started',
            sessions__started: 'Session started',
            sessions__offline_settle: 'Offline session settled',
            shifts__business_day__closed: 'Business day closed',
            shifts__cash_movement__recorded: 'Cash movement recorded',
            shifts__shift__closed: 'Shift closed',
            shifts__shift__opened: 'Shift opened',
            stations__class__create: 'Station class created',
            stations__class__update: 'Station class changed',
            stations__floor_plan__update: 'Floor plan changed',
            stations__station__create: 'Station added',
            stations__station__maintenance: 'Station maintenance changed',
            stations__station__paired: 'Station paired',
            stations__station__power: 'Station powered',
            stations__station__relinked: 'Station re-linked',
            stations__station__update: 'Station changed',
        },
        // What a row is about, by its target type. A type with no copy here reads as its own words.
        targetType: {
            addon: 'Add-on',
            api_key: 'API key',
            audit_log: 'Audit log',
            business_day: 'Business day',
            cash_movement: 'Cash movement',
            device: 'Device',
            floor_plan: 'Floor plan',
            game: 'Game',
            invitation: 'Invitation',
            member: 'Member',
            order: 'Order',
            organization: 'Organisation',
            organization_addon: 'Organisation’s add-on',
            pass: 'Pass',
            player: 'Player',
            rate_card: 'Rate card',
            role: 'Role',
            sale: 'Sale',
            session: 'Session',
            setting_value: 'Setting',
            shift: 'Shift',
            station: 'Station',
            station_class: 'Station class',
            user: 'Person',
            venue: 'Venue',
            zone: 'Zone',
        },
    },
    // Moving a café's players from the software it used before (F-HQ-40). HQ's alone: upload the final export, match its
    // columns, map its PC groups, answer the GST question, review, tick the large balances, import, read the report, undo.
    migration: {
        link: 'Move players from another system',
        linkHint: 'Bring players over from its old software',
        title: 'Move from another system',
        back: 'Back to the organisation',
        intro: 'One move per venue, from the final export taken after the old system was frozen. Players wait as profiles until they claim them; nothing is posted until you import, and an import can be undone for anyone who has not claimed.',
        loadFailed: 'This move could not be loaded.',
        runs: {
            title: 'Moves',
            none: 'No moves yet.',
            start: 'Start a move',
            open: 'Open',
            caption: 'This organisation’s moves, newest first',
            column: { venue: 'Venue', from: 'From', status: 'Status', players: 'Players', started: 'Started' },
        },
        status: { draft: 'Draft', ready: 'Ready to import', importing: 'Importing', completed: 'Imported', undone: 'Undone' },
        steps: {
            source: 'Source',
            upload: 'Upload',
            columns: 'Columns',
            time: 'Time and GST',
            review: 'Review',
            import: 'Import',
            report: 'Report',
        },
        platform: {
            ggleap: 'ggLeap',
            gizmo: 'Gizmo',
            senet: 'Senet',
            icafecloud: 'iCafeCloud',
            pancafe: 'PanCafe Pro',
            smartlaunch: 'SmartLaunch',
            antamedia: 'Antamedia',
            handycafe: 'HandyCafe',
            truecafe: 'TrueCafe',
            other: 'Another system',
        },
        viaCsv: '{{platform}} (via the Clavian CSV)',
        source: {
            title: 'Where the players come from',
            venue: 'Venue',
            venueHint: 'The venue the players played at. Their profiles and balances belong to it.',
            noVenues: 'This organisation has no venue yet. Add one before moving its players.',
            platform: 'Moving from',
            platformHint: 'Every system is read through the Clavian CSV for now: export the players, then shape the file to its columns.',
            start: 'Continue',
            busy: 'This venue already has a move importing. Finish or undo it first.',
        },
        upload: {
            title: 'Upload the export',
            label: 'Export file',
            drop: 'Drop the export here, or choose it',
            hint: 'CSV or XLSX (first sheet), up to 15 MB. Take it after the old system is frozen: no top-ups, no sessions.',
            reading: 'Reading the file…',
            read: '{{name}} · {{rows}} rows · read {{when}}',
            replace: 'Upload a different file',
            continue: 'Continue',
            tooLarge: 'The file is larger than 15 MB.',
            sample: 'Download the Clavian CSV sample',
            formatTitle: 'The Clavian CSV',
            formatHint: 'One row per player. Money in rupees with paise (1,234.50) or in paise; a comma only groups digits, so 1,500 is ₹1,500, never ₹1.50. Time as h:mm, hours, minutes or seconds; dates as YYYY-MM-DD or DD/MM/YYYY. Leave a column out, or a cell empty, when the old system does not have it.',
            columnHeader: 'Column',
            meaningHeader: 'What it holds',
            required: 'Required',
            column: {
                source_id: 'The player’s id in the old system. Re-importing the same id never doubles a balance.',
                username: 'The old username.',
                first_name: 'First name.',
                last_name: 'Last name.',
                email: 'Email address. Used to claim the profile.',
                phone: 'Mobile number, with or without +91.',
                date_of_birth: 'Date of birth. Under-18s are flagged.',
                joined_on: 'When they joined.',
                last_visit_on: 'Their last visit.',
                cash_balance: 'Money they paid for and have not spent.',
                bonus_balance: 'Promotional money. Not carried over: its total is reported.',
                time_balance: 'Prepaid time left. Becomes a time pass.',
                time_pc_groups: 'The PC groups the time was valid on, separated by ;',
                currency: 'INR, if the column exists.',
                banned: 'yes or no.',
                ban_reason: 'Why they were banned.',
                note: 'A staff note.',
                deleted: 'yes for a deleted account: skipped unless it holds a balance.',
            },
        },
        columns: {
            title: 'Match the columns',
            hint: 'Each Clavian field, the column of the file that feeds it, and what the first rows read as. Suggested from the file’s headers: check every one.',
            field: 'Clavian field',
            fileColumn: 'Column in the file',
            reads: 'Reads as',
            notInFile: 'Not in this file',
            unmapped_one: '{{count}} column of the file is not used: {{names}}',
            unmapped_other: '{{count}} columns of the file are not used: {{names}}',
            units: 'How the file writes its values',
            moneyUnit: 'Money',
            timeUnit: 'Time',
            dateOrder: 'Dates',
            money: { major: 'Rupees (1,234.50)', minor: 'Paise (123450)' },
            time: { hms: 'Hours and minutes (2:30)', hours: 'Hours (2.5)', minutes: 'Minutes (150)', seconds: 'Seconds (9000)' },
            date: { dmy: 'Day first (31/12/2026)', mdy: 'Month first (12/31/2026)', ymd: 'Year first (2026/12/31)' },
            usernameRequired: 'Choose the column with the old username.',
            unreadable: 'Cannot read',
            continue: 'Continue',
            fields: {
                sourceId: 'Old player id',
                username: 'Username',
                firstName: 'First name',
                lastName: 'Last name',
                email: 'Email',
                phone: 'Phone',
                dateOfBirth: 'Date of birth',
                joinedOn: 'Joined',
                lastVisitOn: 'Last visit',
                cashBalance: 'Money',
                bonusBalance: 'Bonus money (dropped)',
                timeBalance: 'Prepaid time',
                timePcGroups: 'PC groups of the time',
                currency: 'Currency',
                banned: 'Banned',
                banReason: 'Ban reason',
                note: 'Staff note',
                deleted: 'Deleted',
            },
        },
        time: {
            groupsTitle: 'Where carried-over time can be used',
            groupsHint: 'Prepaid time becomes a time pass on the same kind of PCs as before, valid for 180 days from the switch-over. Choose the station classes each old PC group means.',
            noGroups: 'The file names no PC groups.',
            group: 'Old PC group',
            classes: 'Station classes',
            fallback: 'Time with no PC group given',
            noClasses: 'This venue has no station classes yet. Add them before importing time.',
            switchOn: 'Switch-over day',
            switchOnHint: 'The day the café moved. Ages are taken on it; time passes and the 12-month claim window run from it.',
            gstTitle: 'GST at top-up',
            switchTitle: 'Switch-over day',
            noGst: 'This venue doesn’t charge GST, so balances move as ordinary money.',
            gstQuestion: 'Did the old system charge GST when players topped up?',
            gstYes: 'Yes: GST was paid at top-up',
            gstNo: 'No: GST was charged when money was spent',
            gstHint: 'Ask the café’s accountant. If GST was already paid at top-up, the money is carried over as GST paid and is never taxed again when it is spent; the bill says so.',
            continue: 'Validate and review',
        },
        review: {
            headline: '{{players}} players · {{cash}} carried over ({{taxPaid}} with GST already paid) · {{hours}} as time passes · {{bonus}} bonus dropped',
            headlineNoGst: '{{players}} players · {{cash}} carried over · {{hours}} as time passes · {{bonus}} bonus dropped',
            noGst: 'This venue doesn’t charge GST, so balances move as ordinary money.',
            totals: 'What the import will post',
            players: 'Players',
            cash: 'Money carried over',
            cashTaxPaid: 'Of it, GST already paid',
            time: 'Time carried over',
            bonusDropped: 'Bonus money dropped',
            bonusDroppedHint: 'Tell the café: promotional money does not move.',
            owed: 'Owed to the café, not carried',
            owedHint: 'Negative balances: listed for the counter, never posted.',
            minors: 'Under 18',
            claimByEmail: 'Can claim by email',
            counterOnly: 'Claim at the counter',
            existing: 'Already have a Clavian account',
            statusTitle: 'Rows',
            byStatus: { ok: 'Ready', warning: 'With a warning', error: 'Not imported until fixed', skipped: 'Nothing to import' },
            issuesTitle: 'Findings',
            noIssues: 'Nothing to report.',
            rowsTitle: 'Rows to look at',
            rowsCaption: 'Rows of the file with findings',
            filterAll: 'All findings',
            filterErrors: 'Errors only',
            column: { row: 'Row', player: 'Player', money: 'Money', time: 'Time', status: 'Status', findings: 'Findings' },
            noRows: 'No rows here.',
            more: 'Show more rows',
            largeTitle: 'Large balances',
            largeHint: 'Every balance over ₹5,000 or 20 hours. A balance above the wallet’s maximum needs your tick: check it against the old system’s own report first.',
            largeCaption: 'Large balances, largest first',
            tick: 'Checked',
            tickLabel: 'Checked against the old system: {{player}}',
            noLarge: 'No balance is large enough to list.',
            ticksMissing_one: '{{count}} balance above the wallet’s maximum still needs your tick.',
            ticksMissing_other: '{{count}} balances above the wallet’s maximum still need your tick.',
            walletOff: 'This organisation’s wallet is off. Money cannot be carried over until it is turned on in its settings.',
            gstUnanswered: 'Answer the GST question before importing.',
            notReady: 'Fix the mapping before importing: no row can be imported yet.',
            import: 'Import',
            confirmTitle: 'Import these players?',
            confirmBody: '{{players}} profiles will be created and {{cash}} and {{hours}} of time posted as carried over from {{platform}}. Players claim them later; you can undo anyone who has not.',
            confirm: 'Import',
            cancel: 'Not yet',
        },
        issue: {
            'missing-username': 'No username',
            'bad-amount': 'Money that is not exact',
            'bad-time': 'Time that cannot be read',
            'other-currency': 'Another currency',
            'duplicate-source-id': 'Old id used twice',
            'username-clash': 'Same username twice',
            'time-group-unmapped': 'PC group with no station class',
            'negative-cash': 'Owes the café',
            'negative-time': 'Negative time',
            'over-wallet-cap': 'Above the wallet’s maximum',
            'duplicate-email': 'Shared email',
            'duplicate-phone': 'Shared phone',
            'email-invalid': 'Email not usable',
            'email-blocked': 'Email at a blocked domain',
            'phone-invalid': 'Phone not usable',
            'date-invalid': 'Date not readable',
            'flag-unclear': 'Yes or no not readable',
            'bad-bonus': 'Bonus not readable',
            minor: 'Under 18',
            'house-account': 'Looks like a staff or test account',
            'deleted-in-source': 'Deleted in the old system',
            'large-balance': 'Large balance',
            'bonus-dropped': 'Bonus dropped',
            'existing-account': 'Has a Clavian account',
            'counter-only': 'Claims at the counter',
            'already-imported': 'Already imported',
        },
        severity: { error: 'Error', warning: 'Warning', info: 'Note' },
        rowStatus: { ok: 'Ready', warning: 'Warning', error: 'Error', skipped: 'Skipped', imported: 'Imported', undone: 'Undone', pending: 'Not checked' },
        import: {
            title: 'Importing',
            progress: '{{done}} of {{total}} players imported',
            working: 'Importing in batches. Keep this page open; if it closes, open the move again and resume.',
            failed: 'The import stopped: {{error}}',
            resume: 'Resume the import',
        },
        report: {
            title: 'Imported',
            lead: 'Read back from the ledger: what is held for these players now.',
            players: 'Profiles waiting',
            cash: 'Money held',
            cashTaxPaid: 'Of it, GST already paid',
            time: 'Time held',
            claimed: 'Claimed',
            undone: 'Undone',
            windowEnds: 'Unclaimed profiles wait until {{date}}.',
            download: 'Download the report (CSV)',
            undoAll: 'Undo every unclaimed profile',
            recordsTitle: 'Imported profiles',
            recordsCaption: 'The profiles this move created',
            undoOne: 'Undo',
            undoOneLabel: 'Undo {{player}}',
        },
        // The "account moved" email: under the café's name, to every waiting player with an email.
        movedEmail: {
            title: 'The "account moved" email',
            lead: 'Tells each waiting player, under the café’s name, what was moved for them, how to claim it, and how to ask for their details to be deleted. Players under 18, and players the old system banned, are asked to come to the front desk. Send it at the switch; reminders follow 30 days later and 30 days before the window ends.',
            notSent: 'Not sent yet.',
            sent_one: 'Sent to {{count}} waiting player on {{date}}.',
            sent_other: 'Sent to {{count}} waiting players on {{date}}.',
            send: 'Send the moved email',
            resend: 'Send it again to everyone still waiting',
            noProvider: 'No email provider is set up on this deployment, so nothing was sent.',
            failed: 'The email could not be sent. Try again.',
        },
        undo: {
            allTitle: 'Undo this move?',
            allBody: 'Every profile nobody has claimed is undone and its balances reversed. Claimed profiles stay as they are.',
            oneTitle: 'Undo {{player}}?',
            oneBody: 'The profile is undone and its balances reversed. It can be imported again from a corrected file.',
            reason: 'Why',
            reasonHint: 'Kept in the organisation’s audit log.',
            confirm: 'Undo',
            cancel: 'Keep it',
            done_one: '{{count}} profile undone.',
            done_other: '{{count}} profiles undone.',
            claimedKept_one: '{{count}} claimed profile was kept: {{names}}.',
            claimedKept_other: '{{count}} claimed profiles were kept: {{names}}.',
        },
    },
};
export default hqCopy;
//# sourceMappingURL=hq.js.map